import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  EuiPanel,
  EuiFlexGroup,
  EuiFlexGrid,
  EuiFlexItem,
  EuiBasicTable,
  EuiFormRow,
  EuiFieldNumber,
  EuiTitle,
  EuiSpacer,
  EuiHorizontalRule,
  EuiStat,
  EuiButtonGroup,
  EuiSwitch,
  EuiRange,
  EuiText,
  EuiBadge,
  EuiCallOut,
  EuiFieldText,
  EuiAccordion,
  EuiSelect,
  EuiButton,
  EuiButtonEmpty,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiTextArea,
  EuiLoadingSpinner,
} from "@elastic/eui";
import {
  HardwareConfig,
  OPSANIO_SERVICE_MODELS_ARRAY,
  OpsanioModels,
} from "src/questionnaires";
import { OpsanioApplianceData } from "..";
import { useAnketaI18n } from "src/internationalization";
import { AnketaResourcesEnum } from "src/auth/types";
import { useOpsanioCan } from "../../can";
import { useApi } from "src/api";
import { useAnketaAuth } from "src/auth";
import { useToast } from "src/notifications";
import { getOpsanioScoping, getProbes } from "src/helper/parser";
import { getAiSettings, getFeatures } from "src/helper/parser/opsanio";
import { useCustomerName } from "./customerName";
import {
  contractYearsFromDates,
  CONTRACT_TERMS,
  DEFAULT_TERM_DISCOUNTS,
} from "./contractTerm";
import {
  WishedPriceBasis,
  WISHED_PRICE_BASES,
  DEFAULT_DEVELOPMENT,
  DEFAULT_EFFORT,
  DEFAULT_SERVICES,
  DEFAULT_BUNDLE_FEES,
  DEFAULT_COVERAGE,
  DEFAULT_SURCHARGES,
  DEFAULT_ON_SITE,
  DEFAULT_CUSTOM_DEVELOPMENT,
  CustomDevelopmentConfig,
  DEFAULT_UPDATE_DELIVERY,
  FEATURE_IDS,
  FeatureId,
  FeatureLine,
  Features,
  featuresFromQuestionnaire,
  UPDATE_METHODS,
  UpdateDeliveryConfig,
  UpdateMethodId,
  OnSiteConfig,
  COVERAGE_TIERS,
  outOfHoursProfile,
  SurchargeRates,
  CoverageConfig,
  CoverageTierId,
  DealRating,
  DevelopmentConfig,
  applyServiceBundle,
  priceAppliance,
  PricingInputs,
  SERVICE_BUNDLE_IDS,
  SERVICE_IDS,
  ServiceBundleId,
  NamedServiceBundle,
  ServiceId,
  ServiceLine,
  ServiceUnit,
  AdjustedPriceLine,
  DEFAULT_PRICE_WEIGHTS,
  MAX_PRICE_WEIGHT,
  PriceLineKey,
  PriceWeights,
} from "./costModel";
import {
  HW_DAC_KEY,
  HW_PROBES_KEY,
  HW_SERVERS_KEY,
  HW_SWITCHES_KEY,
  parseDeviceIds,
} from "../hardware";

/** How each deal rating is shown. Red is reserved for a loss-making price. */
const DEAL_COLOR: Record<DealRating, "success" | "warning" | "danger"> = {
  good: "success",
  medium: "warning",
  bad: "warning",
  negative: "danger",
};

const DEAL_ICON: Record<DealRating, string> = {
  good: "check",
  medium: "alert",
  bad: "alert",
  negative: "crossInACircleFilled",
};

/** Translation prefix for this screen, as every other flyout tab declares. */
const i18n = "anketa.opsanio.appliance.flyout.cost";

// Content key of the Montagestandort (cpe) multi-location address form. Its
// fact persists as a JSON-stringified array of sites.
const CPE_LOCATIONS_KEY = "cpe.location";

// The cost calculator extends the shared hardware config with an editable
// server count (server total = cost × count) and a dedicated DAC-cable line
// (model from NetBox, editable count + cost) alongside patch cables and
// transceivers.
interface CostHardwareConfig extends Omit<HardwareConfig, "server" | "cable"> {
  server: HardwareConfig["server"] & { count: number };
  cable: HardwareConfig["cable"] & {
    dac: { model: string; cost: number; count: number };
  };
  // Customer-site monitoring probes (count prefilled from the questionnaire's
  // networks.probes.* facts; model and cost are manual).
  probe: { model: string; cost: number; count: number };
}

// Default per-unit hardware prices used to seed the calculator. The quantity
// of each line is prefilled from NetBox / the questionnaire (and stays
// editable); these are the per-unit costs multiplied by that quantity.
/**
 * Last-resort per-unit prices, used only until the NetBox lookup answers - and
 * for the probe, which is not a NetBox device type and has no price there.
 *
 * Everything else is overwritten by the device type's `purchase_price` (see the
 * cost-basis effect), so these no longer decide what a quotation says.
 */
const DEFAULT_HARDWARE_COSTS = {
  server: 5123,
  switch: 1690,
  dac: 13,
  probe: 324,
  /** One per server, and they cost about this. Not a NetBox device type. */
  patch: 2,
};

/**
 * What a completed proof of concept is credited against the build, in euro.
 * Mirrors `POC_OFFSET_EUR` on the server, which the cost-basis response also
 * carries.
 */
const POC_OFFSET_EUR = 3000;

/** Money, in the format the rest of this screen uses. */
const euro = (value: number): string =>
  value.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** One line of `GET /netbox/hardware/cost-basis`. */
interface CostBasisLine {
  kind: "server" | "switch" | "dac" | "probe";
  count: number;
  unitPrice: number | null;
  total: number | null;
  model: string;
  source: "assigned" | "default";
  fullyPriced: boolean;
}

/**
 * One row of the contract-term comparison.
 *
 * Every term priced on the same basis as the headline figure, so the selected
 * row and the summary bar directly above it state the same number.
 */
interface TermComparisonRow {
  years: number;
  /** Term discount applied, in percent. */
  discount: number;
  monthly: number;
  yearly: number;
  /** What the whole term comes to. */
  total: number;
  /** Against the same undiscounted price the headline savings figure uses. */
  savings: number;
}

interface CostCalculatorConfig {
  hardware: CostHardwareConfig;
  /**
   * Credit for a proof of concept the customer has already paid for, offset
   * once against the hardware rather than discounted from the running fee.
   * Prefilled from the questionnaire and editable, like every other figure here.
   */
  pocOffset: number;
  distributorMarginPercent: number;
  hourlyRate: number;
  /** Term discount in percent, keyed by contract years. */
  termDiscounts: Record<number, number>;
  /** People effort: commissioning and the services the deal includes. */
  effort: PricingInputs["effort"];
  /** Service hours (business days, extended, 24x7) and their fixed fees. */
  coverage: CoverageConfig;
  /** On-site visits, an optional extension of the remote services. */
  onSite: OnSiteConfig;
  /** How updates reach the appliance: online, via a gateway, or by USB. */
  updateDelivery: UpdateDeliveryConfig;
  /** The platform features the deal includes, each priced. */
  features: Features;
  /** How much each price line may give way to the asking price. */
  priceWeights: PriceWeights;
  /** A development budget for customer requests. */
  customDevelopment: CustomDevelopmentConfig;
  /** The share of the platform's build cost this appliance recovers. */
  development: DevelopmentConfig;
  /**
   * What the customer has said they want to pay, in whichever unit they said
   * it. 0 means nothing has been named yet - the indicator stays hidden rather
   * than ranking a price nobody has offered.
   */
  wishedPrice: number;
  /** Whether that figure is per month, per year, or for the whole contract. */
  wishedPriceBasis: WishedPriceBasis;
  upfrontPaymentDiscount: number;
  profitMargin: number;
}

/**
 * One input group: a bordered panel carrying an accordion whose header keeps
 * the group's figure readable while it is collapsed.
 *
 * One component rather than four hand-written panels, because the design
 * fixes one shape for all four - title on the left, figure on the right - and
 * four copies drift apart the moment one of them gains a field.
 */
const CostGroup = ({
  id,
  title,
  note,
  figure,
  figureColor,
  initialIsOpen = false,
  children,
}: {
  id: string;
  title: string;
  note?: string;
  figure: string;
  figureColor?: "default" | "success";
  initialIsOpen?: boolean;
  children: React.ReactNode;
}) => (
  <EuiPanel hasBorder hasShadow={false} paddingSize="none">
    <EuiAccordion
      id={id}
      initialIsOpen={initialIsOpen}
      paddingSize="m"
      buttonContent={
        <EuiTitle size="xs">
          <h3>{title}</h3>
        </EuiTitle>
      }
      extraAction={
        <EuiFlexGroup
          alignItems="center"
          gutterSize="s"
          responsive={false}
          style={{ paddingRight: 12 }}
        >
          {note && (
            <EuiFlexItem grow={false}>
              <EuiText size="s" color="subdued">
                {note}
              </EuiText>
            </EuiFlexItem>
          )}
          <EuiFlexItem grow={false}>
            <EuiText
              size="s"
              color={figureColor === "success" ? "success" : "default"}
            >
              <strong>{figure}</strong>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      }
    >
      {children}
    </EuiAccordion>
  </EuiPanel>
);

/**
 * A heading inside a group. The body carries exactly two levels - group and
 * sub-group - so a nested accordion never has to become a third.
 */
const SubGroup = ({ label }: { label: string }) => (
  <>
    <EuiText size="xs" color="subdued">
      <h4 style={{ textTransform: "uppercase", letterSpacing: "0.03em" }}>
        {label}
      </h4>
    </EuiText>
    <EuiSpacer size="s" />
  </>
);

export default function OpsanioApplianceCostCalculator({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}) {
  const { translate } = useAnketaI18n();
  const { Get, Post } = useApi();
  const { addToast } = useToast();
  const { keycloak } = useAnketaAuth();
  // The approval request mails a priced offer: opsanio/documentation · create.
  const canRequestApproval = useOpsanioCan()(
    AnketaResourcesEnum.opsanioDocumentation,
    "create",
  );
  // The service model may be missing on the appliance or carry a value that
  // isn't in the map (legacy/incomplete data). Fall back to the "starter"
  // config so the calculator still renders with sensible zero defaults instead
  // of crashing on `defaultHardwareConfig.server`.
  const defaultHardwareConfig: OpsanioModels =
    OPSANIO_SERVICE_MODELS_ARRAY.get(appliance.content?.service_model) ??
    OPSANIO_SERVICE_MODELS_ARRAY.get("starter")!;

  // Sizing derived from the Montagestandort (cpe) multi-location address form.
  // The per-site sizing table (typ/users/server/fw) no longer exists, so the
  // only metric available is the number of sites, which prefills the switch
  // quantity and is surfaced as a read-only summary.
  const locationSizing = useMemo(() => {
    // The address form persists as a JSON-stringified array of sites; tolerate
    // both a parsed array and a string.
    const raw = appliance.content?.[CPE_LOCATIONS_KEY]?.value;
    let locations: unknown[] = [];
    if (Array.isArray(raw)) {
      locations = raw;
    } else if (typeof raw === "string" && raw.trim() !== "") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) locations = parsed;
      } catch {
        locations = [];
      }
    }
    return { sites: locations.length };
  }, [appliance.content]);

  // Device IDs and counts selected on the appliance via the NetBox hardware
  // selectors. The counts prefill the (editable) quantity fields; the IDs are
  // used to fetch the device models from NetBox below.
  const netboxCounts = useMemo(() => {
    const serverIds = parseDeviceIds(appliance.content?.[HW_SERVERS_KEY]);
    const switchIds = parseDeviceIds(appliance.content?.[HW_SWITCHES_KEY]);
    const dacIds = parseDeviceIds(appliance.content?.[HW_DAC_KEY]);
    const probeIds = parseDeviceIds(appliance.content?.[HW_PROBES_KEY]);
    return {
      serverIds,
      switchIds,
      dacIds,
      probeIds,
      servers: serverIds.length,
      switches: switchIds.length,
      dac: dacIds.length,
      probes: probeIds.length,
    };
  }, [appliance.content]);

  /**
   * Did this customer run a proof of concept? `general.information.poc` is the
   * questionnaire's own answer; the calculator credits it once against the
   * hardware (see `pocOffset`) because the customer has already paid for it.
   */
  const wantPoc = useMemo<boolean>(
    () => getOpsanioScoping(appliance.content ?? {}).poc.wantPoc,
    [appliance.content],
  );

  // Number of customer-site probes configured on the appliance. Probes are not
  // NetBox hardware; they come from the questionnaire's networks.probes.* facts.
  /**
   * What the questionnaire's Features section switches on. It prefills the
   * feature lines and stays visible beside them, so a price built on a
   * different set than the appliance runs is noticed. AI is priced at the
   * monthly spend cap agreed for it, when budgets are on.
   */
  const questionnaireFeatures = useMemo<Features>(() => {
    let flags = null;
    let aiBudget: number | null = null;
    try {
      // The VAPID keys are irrelevant here and getFeatures wants them.
      flags = getFeatures(appliance.content ?? {}, { private: "", public: "" });
    } catch {
      flags = null;
    }
    try {
      const ai = getAiSettings(appliance.content ?? {});
      aiBudget = ai?.budgets?.enabled ? ai.budgets.monthly.limit : null;
    } catch {
      aiBudget = null;
    }
    return featuresFromQuestionnaire(flags, aiBudget);
  }, [appliance.content]);

  const probeCount = useMemo(
    () => getProbes(appliance.content ?? {}).length,
    [appliance.content],
  );

  const [config, setConfig] = useState<CostCalculatorConfig>({
    hardware: {
      server: {
        ...defaultHardwareConfig.server,
        cost: DEFAULT_HARDWARE_COSTS.server,
        // Prefill the server count from the selected NetBox server devices.
        count: netboxCounts.servers > 0 ? netboxCounts.servers : 1,
      },
      switch: {
        ...defaultHardwareConfig.switch,
        cost: DEFAULT_HARDWARE_COSTS.switch,
        // Prefill switch count from the selected NetBox switch devices, then
        // fall back to the number of installation sites, then the preset.
        count:
          netboxCounts.switches > 0
            ? netboxCounts.switches
            : locationSizing.sites > 0
              ? locationSizing.sites
              : defaultHardwareConfig.switch.count,
      },
      cable: {
        // One patch cable per server - that is how an appliance is cabled - at
        // roughly two euro apiece. Patch cables are not NetBox devices, so
        // nothing downstream can correct this the way it corrects the priced
        // lines; it stays editable like every other field.
        patch: {
          ...defaultHardwareConfig.cable.patch,
          cost: DEFAULT_HARDWARE_COSTS.patch,
          count: netboxCounts.servers > 0 ? netboxCounts.servers : 1,
        },
        transceiver: { ...defaultHardwareConfig.cable.transceiver },
        // Dedicated DAC line: count prefilled from the selected NetBox DAC
        // cables; model is filled in once NetBox responds; cost is manual.
        dac: { model: "", cost: DEFAULT_HARDWARE_COSTS.dac, count: netboxCounts.dac },
      },
      // Probe line: count prefilled from the configured customer-site probes.
      probe: {
        model: "",
        cost: DEFAULT_HARDWARE_COSTS.probe,
        // The units actually handed out where the questionnaire names them,
        // otherwise how many the site is configured for.
        count: netboxCounts.probes > 0 ? netboxCounts.probes : probeCount,
      },
    },
    // Prefilled below once the questionnaire's PoC answer is known.
    pocOffset: 0,
    distributorMarginPercent: 10,
    hourlyRate: 140,
    termDiscounts: { ...DEFAULT_TERM_DISCOUNTS },
    effort: {
      ...DEFAULT_EFFORT,
      services: { ...DEFAULT_SERVICES },
      bundleFees: { ...DEFAULT_BUNDLE_FEES },
    },
    coverage: {
      ...DEFAULT_COVERAGE,
      monthlyFees: { ...DEFAULT_COVERAGE.monthlyFees },
      surcharges: { ...DEFAULT_SURCHARGES },
    },
    onSite: { ...DEFAULT_ON_SITE },
    updateDelivery: {
      ...DEFAULT_UPDATE_DELIVERY,
      gateway: { ...DEFAULT_UPDATE_DELIVERY.gateway },
      airgap: { ...DEFAULT_UPDATE_DELIVERY.airgap },
    },
    features: questionnaireFeatures,
    priceWeights: {
      lines: { ...DEFAULT_PRICE_WEIGHTS.lines },
      features: { ...DEFAULT_PRICE_WEIGHTS.features },
    },
    customDevelopment: { ...DEFAULT_CUSTOM_DEVELOPMENT },
    development: { ...DEFAULT_DEVELOPMENT },
    wishedPrice: 0,
    wishedPriceBasis: "monthly",
    upfrontPaymentDiscount: 8,
    profitMargin: 20,
  });

  /**
   * The contract term, prefilled from the start and end dates agreed in the
   * questionnaire (`general.date.dateStart` / `dateEnd`, already parsed onto
   * `common.general.dates`). A questionnaire with no dates keeps one year.
   */
  const questionnaireTerm = useMemo<number | null>(
    () =>
      contractYearsFromDates(
        appliance.common?.general?.dates?.start,
        appliance.common?.general?.dates?.end,
      ),
    [appliance.common],
  );

  const [contractYears, setContractYears] = useState<number>(
    () => questionnaireTerm ?? 1,
  );
  const [upfrontPayment, setUpfrontPayment] = useState<boolean>(false);

  /**
   * Prefill the hardware lines from NetBox: the device types actually assigned
   * to this appliance, their quantities, and their `purchase_price`.
   *
   * One call rather than three model lookups, because the same endpoint that
   * knows which device types were selected is the one that can price them. A
   * questionnaire with nothing assigned gets the standard build (2 servers, 1
   * switch, 5 DAC cables) priced instead, marked `source: "default"` so the
   * assumption is visible rather than passed off as a selection.
   *
   * **All prices include sales tax** - that is how NetBox maintains them.
   *
   * Applied once, so a user's manual edits are never overwritten.
   */
  const [costBasis, setCostBasis] = useState<CostBasisLine[] | null>(null);
  const appliedBasisRef = useRef(false);
  useEffect(() => {
    const token = keycloak?.token;
    if (typeof token !== "string" || appliedBasisRef.current) return;

    let cancelled = false;

    (async () => {
      const query = new URLSearchParams({
        server: netboxCounts.serverIds.join(","),
        switch: netboxCounts.switchIds.join(","),
        dac: netboxCounts.dacIds.join(","),
        probe: netboxCounts.probeIds.join(","),
      });
      const { success, data } = await Get<any>({
        path: `v2/netbox/hardware/cost-basis?${query.toString()}`,
        responseType: "json",
        token,
      });
      // The API client never throws on HTTP errors - `success` is the check.
      // A failed lookup leaves the calculator on its own defaults rather than
      // refusing to open; the fields are editable either way.
      if (cancelled || !success) return;

      const lines: CostBasisLine[] = (data as any)?.data?.lines ?? [];
      if (lines.length === 0) return;
      const byKind = Object.fromEntries(lines.map((line) => [line.kind, line]));
      appliedBasisRef.current = true;
      setCostBasis(lines);

      setConfig((prev) => {
        // A line NetBox could not price keeps the previous figure: 0 € would
        // quietly understate a quotation, which is the one direction nobody
        // checks.
        const apply = <T extends { model: string; cost: number; count: number }>(
          line: CostBasisLine | undefined,
          current: T,
        ): T => ({
          ...current,
          model: line?.model || current.model,
          cost: typeof line?.unitPrice === "number" ? line.unitPrice : current.cost,
          count: line && line.count > 0 ? line.count : current.count,
        });

        const server = apply(byKind.server, prev.hardware.server);
        return {
          ...prev,
          hardware: {
            ...prev.hardware,
            server,
            switch: apply(byKind.switch, prev.hardware.switch),
            cable: {
              ...prev.hardware.cable,
              // One per server. Follows the server count as it is corrected
              // here - on a fresh opportunity that is the standard build's two,
              // not the one the initial state guessed at.
              patch: {
                ...prev.hardware.cable.patch,
                count: server.count > 0 ? server.count : prev.hardware.cable.patch.count,
              },
              dac: apply(byKind.dac, prev.hardware.cable.dac),
            },
            // Probes assigned in NetBox win; otherwise the questionnaire's own
            // probe count stands and only the price is taken from the device
            // type, because the standard build assumes no probes.
            probe: apply(byKind.probe, prev.hardware.probe),
          },
        };
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [Get, keycloak, netboxCounts]);

  /**
   * Credit the proof of concept the customer has already paid for.
   *
   * Prefilled from the questionnaire's own answer and then left alone, so a
   * salesperson can waive or adjust it without the questionnaire arguing back.
   */
  const appliedPocRef = useRef(false);
  useEffect(() => {
    if (appliedPocRef.current) return;
    appliedPocRef.current = true;
    if (!wantPoc) return;
    setConfig((prev) => ({ ...prev, pocOffset: POC_OFFSET_EUR }));
  }, [wantPoc]);

  const yearLabel = (years: number): string =>
    years === 1
      ? translate("anketa.opsanio.appliance.flyout.cost.years.one", {
          values: { count: years },
          defaultMessage: "{{count}} year",
        })
      : translate("anketa.opsanio.appliance.flyout.cost.years.other", {
          values: { count: years },
          defaultMessage: "{{count}} years",
        });

  const yearOptions = CONTRACT_TERMS.map((years) => ({
    id: `${years}`,
    label: yearLabel(years),
  }));

  const updateSetupHours = (value: number) =>
    setConfig((prev) => ({
      ...prev,
      effort: { ...prev.effort, initialSetupHours: value },
    }));

  const updateService = (id: ServiceId, patch: Partial<ServiceLine>) =>
    setConfig((prev) => ({
      ...prev,
      effort: {
        ...prev.effort,
        services: {
          ...prev.effort.services,
          [id]: { ...prev.effort.services[id], ...patch },
        },
      },
    }));

  const selectServiceBundle = (bundle: ServiceBundleId) =>
    setConfig((prev) => ({
      ...prev,
      effort: {
        ...prev.effort,
        services: applyServiceBundle(prev.effort.services, bundle),
      },
    }));

  const serviceLabel = (id: ServiceId): string =>
    translate(`${i18n}.service.${id}.title`, { defaultMessage: id });

  const updateBundleFee = (bundle: NamedServiceBundle, fee: number) =>
    setConfig((prev) => ({
      ...prev,
      effort: {
        ...prev.effort,
        bundleFees: { ...prev.effort.bundleFees, [bundle]: fee },
      },
    }));

  const selectCoverage = (tier: CoverageTierId) =>
    setConfig((prev) => ({ ...prev, coverage: { ...prev.coverage, tier } }));

  const updateCoverageFee = (tier: CoverageTierId, fee: number) =>
    setConfig((prev) => ({
      ...prev,
      coverage: {
        ...prev.coverage,
        monthlyFees: { ...prev.coverage.monthlyFees, [tier]: fee },
      },
    }));

  const updateCoverage = (
    field: "outOfHoursHours" | "holidaysPerYear",
    value: number,
  ) =>
    setConfig((prev) => ({
      ...prev,
      coverage: { ...prev.coverage, [field]: value },
    }));

  const updateOnSite = <K extends keyof OnSiteConfig>(
    field: K,
    value: OnSiteConfig[K],
  ) =>
    setConfig((prev) => ({
      ...prev,
      onSite: { ...prev.onSite, [field]: value },
    }));

  const updateCustomDevelopment = <K extends keyof CustomDevelopmentConfig>(
    field: K,
    value: CustomDevelopmentConfig[K],
  ) =>
    setConfig((prev) => ({
      ...prev,
      customDevelopment: { ...prev.customDevelopment, [field]: value },
    }));

  const updateWeight = (key: PriceLineKey, weight: number) =>
    setConfig((prev) => ({
      ...prev,
      priceWeights:
        key.kind === "feature"
          ? {
              ...prev.priceWeights,
              features: { ...prev.priceWeights.features, [key.id]: weight },
            }
          : {
              ...prev.priceWeights,
              lines: { ...prev.priceWeights.lines, [key.id]: weight },
            },
    }));

  const priceLineLabel = (key: PriceLineKey): string =>
    key.kind === "feature"
      ? translate(`${i18n}.features.${key.id}`, { defaultMessage: key.id })
      : translate(`${i18n}.adjust.line.${key.id}`, { defaultMessage: key.id });

  const updateFeature = (id: FeatureId, patch: Partial<FeatureLine>) =>
    setConfig((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [id]: { ...prev.features[id], ...patch },
      },
    }));

  const selectUpdateMethod = (method: UpdateMethodId) =>
    setConfig((prev) => ({
      ...prev,
      updateDelivery: { ...prev.updateDelivery, method },
    }));

  const updateDeliveryField = (
    group: "gateway" | "airgap",
    field: string,
    value: number,
  ) =>
    setConfig((prev) => ({
      ...prev,
      updateDelivery: {
        ...prev.updateDelivery,
        [group]: { ...prev.updateDelivery[group], [field]: value },
      },
    }));

  const updateSurcharge = (field: keyof SurchargeRates, value: number) =>
    setConfig((prev) => ({
      ...prev,
      coverage: {
        ...prev.coverage,
        surcharges: { ...prev.coverage.surcharges, [field]: value },
      },
    }));

  /**
   * Whether the selected window reaches outside business hours at all - and so
   * whether call-outs there, and the surcharges on them, are part of the deal.
   */
  const coversOutOfHours = useMemo(
    () =>
      outOfHoursProfile(
        config.coverage.tier,
        config.coverage.surcharges,
        config.coverage.holidaysPerYear,
      ).hoursPerYear > 0,
    [config.coverage],
  );

  const coverageLabel = (tier: CoverageTierId): string =>
    translate(`${i18n}.coverage.${tier}.title`, { defaultMessage: tier });

  const bundleLabel = (bundle: ServiceBundleId): string =>
    translate(`${i18n}.bundle.${bundle}`, { defaultMessage: bundle });

  const updateDevelopment = (field: keyof DevelopmentConfig, value: number) =>
    setConfig((prev) => ({
      ...prev,
      development: { ...prev.development, [field]: value },
    }));

  const updateConfig = (
    key: keyof Omit<CostCalculatorConfig, "hardware">,
    value: number
  ) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const updateHardware = (
    category: keyof CostHardwareConfig,
    field: string,
    value: string | number
  ) => {
    setConfig((prev) => {
      const newHardware = { ...prev.hardware };

      if (category === "cable") {
        const [subCategory, subField] = field.split(".");
        newHardware.cable = {
          ...newHardware.cable,
          [subCategory]: {
            ...newHardware.cable[subCategory as keyof typeof newHardware.cable],
            [subField]: value,
          },
        };
      } else if (category === "server") {
        newHardware.server = {
          ...newHardware.server,
          [field]: value,
        } as typeof newHardware.server;
      } else if (category === "switch") {
        newHardware.switch = {
          ...newHardware.switch,
          [field]: value,
        } as typeof newHardware.switch;
      } else if (category === "probe") {
        newHardware.probe = {
          ...newHardware.probe,
          [field]: value,
        } as typeof newHardware.probe;
      }

      return { ...prev, hardware: newHardware };
    });
  };
  /**
   * The whole calculation, from the shared model.
   *
   * Not computed here any more: the offer PDF is priced from the same inputs on
   * the server, and an approval document that disagreed with the screen it was
   * raised from would be worse than no document. One implementation, two
   * callers.
   */
  const priced = useMemo(
    () =>
      priceAppliance({
        hardware: config.hardware,
        pocOffset: config.pocOffset,
        distributorMarginPercent: config.distributorMarginPercent,
        hourlyRate: config.hourlyRate,
        effort: config.effort,
        coverage: config.coverage,
        onSite: config.onSite,
        updateDelivery: config.updateDelivery,
        features: config.features,
        priceWeights: config.priceWeights,
        customDevelopment: config.customDevelopment,
        development: config.development,
        profitMargin: config.profitMargin,
        termDiscounts: config.termDiscounts,
        upfrontPaymentDiscount: config.upfrontPaymentDiscount,
        upfrontPayment,
        contractYears,
        wishedPrice: config.wishedPrice,
        wishedPriceBasis: config.wishedPriceBasis,
      }),
    [config, contractYears, upfrontPayment],
  );

  const hardwareCosts = priced.hardware;
  const calculations = priced;

  /**
   * Every contract term priced the way the headline figure is priced.
   *
   * The card grid this replaces rebuilt the monthly price from the hardware and
   * the operating cost alone, leaving out the commissioning effort and the
   * development share. The selected term's card therefore disagreed with the
   * stat it sat next to. Once the comparison is a table directly under the
   * summary bar, that gap is no longer survivable: one formula, applied per
   * term, is the only arrangement in which both can be read at once.
   */
  const termComparison = useMemo(() => {
    // The price before any term or upfront discount, taken from the model
    // rather than rebuilt here - it is the same baseline `totalSavings`
    // measures against, so the table's savings column and the headline savings
    // figure cannot contradict each other.
    const { undiscountedMonthly } = priced;

    const rows: TermComparisonRow[] = CONTRACT_TERMS.map((years) => {
      const discount = config.termDiscounts[years] ?? 0;
      const monthly =
        undiscountedMonthly *
        (1 - discount / 100) *
        (upfrontPayment ? 1 - config.upfrontPaymentDiscount / 100 : 1);
      return {
        years,
        discount,
        monthly,
        yearly: monthly * 12,
        total: monthly * 12 * years,
        savings: (undiscountedMonthly - monthly) * 12 * years,
      };
    });

    return { rows, undiscountedMonthly };
  }, [
    priced,
    config.termDiscounts,
    config.upfrontPaymentDiscount,
    upfrontPayment,
  ]);

  /** The customer is willing to pay more than calculated; the offer quotes it. */
  const uplifted = priced.upliftPerMonth > 0.005;

  /** A price the customer has actually named. 0 means nobody has offered one. */
  const hasWishedPrice = config.wishedPrice > 0;

  // -------------------------------------------------------------------------
  // Asking for approval
  // -------------------------------------------------------------------------

  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalNote, setApprovalNote] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  /**
   * Which of the two attached documents the dialog shows: the approval
   * document, or the version the customer receives once it is granted. Both
   * are sent; the switch only decides which one is on screen.
   */
  const [previewVersion, setPreviewVersion] = useState<"internal" | "customer">(
    "internal",
  );
  /**
   * Who the request will actually reach: the configured offer mailboxes, and
   * nobody else. `OFFER_INTERNAL_EMAIL` may name several.
   *
   * Fetched rather than described - "the internal offer mailbox" is not
   * something a sender can check, and this mail carries a price. `null` while it
   * is still loading. The server resolves it the same way it addresses it, so
   * what is shown here is what goes out.
   */
  const [recipients, setRecipients] = useState<{
    recipients: string[];
    configured: boolean;
  } | null>(null);
  // Set when the recipient lookup failed, so the list shows a message instead
  // of spinning forever.
  const [recipientsFailed, setRecipientsFailed] = useState<boolean>(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  /**
   * Who the offer is for.
   *
   * Prefilled from the appliance, which is the only name the questionnaire
   * carries - there is no customer or organisation field on it, and
   * `metadata.project` is a slug off the VPC name, not a company. The appliance
   * name is therefore a starting point, not the answer: left alone, the offer
   * reads "elk-mimosa betreibt die opsanio-Appliance ...", naming the appliance
   * as though it were the customer. Editable, so the person raising the
   * approval can put the actual customer on the document.
   */
  const applianceName =
    appliance.displayName || appliance.common?.metadata?.name || "";
  // Shared with the documentation tab, so the offer and the documentation it
  // is sent with name the same customer.
  const [customerName, setCustomerName] = useCustomerName(appliance.id, applianceName);

  /**
   * What the downloaded copy is called.
   *
   * Built here rather than read from the response: the download is the blob the
   * preview already fetched, and naming it after the appliance keeps it
   * recognisable in a downloads folder. The document carries its own offer
   * number on the cover either way.
   */
  const offerFileName = `${
    previewVersion === "customer" ? "Angebot" : "Freigabevorlage"
  }-${
    (appliance.common?.metadata?.name || customerName || "appliance")
      .replace(/[^A-Za-z0-9-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "appliance"
  }.pdf`;

  /**
   * What the server is asked to price.
   *
   * The **inputs**, never the figures on screen: the offer is recomputed
   * server-side from exactly these, so the document and this screen cannot
   * disagree about a price somebody is being asked to approve.
   */
  const offerPayload = (note: string) => ({
    customerName,
    note: note.trim() || undefined,
    pricing: {
      hardware: {
        server: {
          cost: config.hardware.server.cost,
          count: config.hardware.server.count,
        },
        switch: {
          cost: config.hardware.switch.cost,
          count: config.hardware.switch.count,
        },
        probe: {
          cost: config.hardware.probe.cost,
          count: config.hardware.probe.count,
        },
        cable: {
          patch: {
            cost: config.hardware.cable.patch.cost,
            count: config.hardware.cable.patch.count,
          },
          transceiver: {
            cost: config.hardware.cable.transceiver.cost,
            count: config.hardware.cable.transceiver.count,
          },
          dac: {
            cost: config.hardware.cable.dac.cost,
            count: config.hardware.cable.dac.count,
          },
        },
      },
      pocOffset: config.pocOffset,
      distributorMarginPercent: config.distributorMarginPercent,
      hourlyRate: config.hourlyRate,
      effort: config.effort,
      coverage: config.coverage,
      onSite: config.onSite,
      updateDelivery: config.updateDelivery,
      features: config.features,
      priceWeights: config.priceWeights,
      customDevelopment: config.customDevelopment,
      development: config.development,
      profitMargin: config.profitMargin,
      termDiscounts: config.termDiscounts,
      upfrontPaymentDiscount: config.upfrontPaymentDiscount,
      upfrontPayment,
      contractYears,
      wishedPrice: config.wishedPrice,
      wishedPriceBasis: config.wishedPriceBasis,
    } as PricingInputs,
  });

  const approvalPath = `v2/documentation/offer/${appliance.catalogId}/${appliance.id}`;

  /**
   * Render the document the approver would receive and show it.
   *
   * The same endpoint, builder and bytes as the send - a preview that could
   * differ from what is sent would be worse than no preview at all.
   */
  const loadPreview = async (
    note: string,
    version: "internal" | "customer" = previewVersion,
  ) => {
    const token = keycloak?.token;
    if (typeof token !== "string") return;
    setPreviewing(true);
    // Release the previous document before replacing it; an operator who edits
    // the note a few times would otherwise leak a blob per attempt.
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    const resp = await Post<Blob>({
      path: `${approvalPath}/preview${version === "customer" ? "?version=customer" : ""}`,
      value: offerPayload(note),
      token,
      responseType: "blob",
    });
    setPreviewing(false);
    // The API client never throws on HTTP errors - `success` is the check.
    if (!resp.success || !(resp.data instanceof Blob)) {
      addToast({
        title: translate(`${i18n}.approval.previewFailed`, {
          defaultMessage: "Could not render the offer",
        }),
        color: "danger",
        text: <p>{resp.error?.message ?? ""}</p>,
      });
      return;
    }
    setPreviewUrl(
      URL.createObjectURL(resp.data.slice(0, resp.data.size, "application/pdf")),
    );
  };

  const loadRecipients = async () => {
    const token = keycloak?.token;
    if (typeof token !== "string") return;
    const resp = await Get<any>({
      path: `${approvalPath}/recipients`,
      responseType: "json",
      token,
    });
    const data = (resp.data as any)?.data;
    if (!resp.success || !data) {
      setRecipientsFailed(true);
      return;
    }
    setRecipients(data);
  };

  const openApproval = () => {
    setApprovalOpen(true);
    if (customerName.trim() === "") setCustomerName(applianceName);
    setRecipients(null);
    setRecipientsFailed(false);
    void loadRecipients();
    void loadPreview(approvalNote);
  };

  const closeApproval = () => {
    setApprovalOpen(false);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  };

  const sendApproval = async () => {
    const token = keycloak?.token;
    if (typeof token !== "string") return;
    setSending(true);
    const resp = await Post<any>({
      path: approvalPath,
      value: offerPayload(approvalNote),
      token,
    });
    setSending(false);
    if (!resp.success) {
      addToast({
        title: translate(`${i18n}.approval.failed`, {
          defaultMessage: "Approval request not sent",
        }),
        color: "danger",
        text: <p>{resp.error?.message ?? ""}</p>,
      });
      return;
    }
    addToast({
      title: translate(`${i18n}.approval.sent`, {
        defaultMessage: "Approval requested",
      }),
      color: "success",
      text: (
        <p>
          {translate(`${i18n}.approval.sentTo`, {
            values: {
              recipient: ((resp.data as any)?.data?.sent ?? []).join(", "),
            },
            defaultMessage: "Sent to {{recipient}}.",
          })}
          {/*
            The transport's message id. "Sent" only ever means the mail was
            handed to Exchange - a later bounce arrives at the mailbox, not
            here - so the id is shown: it is what an Exchange message trace is
            searched by when a request does not turn up.
          */}
          {(resp.data as any)?.data?.messageId && (
            <>
              <br />
              <EuiText size="xs" color="subdued">
                <code>{(resp.data as any).data.messageId}</code>
              </EuiText>
            </>
          )}{" "}
          {/* Name the copies too - the toast is the last chance to notice the
              request went somewhere unexpected. */}
        </p>
      ),
    });
    // The documentation is attached best effort; say so when it could not be,
    // rather than letting the approver find out from a missing attachment.
    const documentationError = (resp.data as any)?.data?.documentationError;
    if (documentationError) {
      addToast({
        title: translate(`${i18n}.approval.noDocumentation`, {
          defaultMessage: "Note on the appliance documentation",
        }),
        color: "warning",
        text: <p>{documentationError}</p>,
      });
    }
    closeApproval();
  };

  /**
   * Whether this price loses money every month of the term.
   *
   * It does **not** block the request. Selling below cost is sometimes a
   * deliberate decision - a strategic account, a reference customer, a foot in
   * the door - and that decision belongs to the person being asked to approve
   * it, not to this screen. What the calculator owes them is that the loss is
   * impossible to miss: it is called out before sending, it is the first line
   * of the mail, and the offer states it in its own risks section.
   */
  const lossMaking = hasWishedPrice && calculations.deal.rating === "negative";

  /**
   * Keep the preview on what the dialog says. The customer name and the
   * justification both appear in the document, and a preview that still shows
   * the previous name reads as though the edit had not taken - so it is
   * re-rendered a moment after typing stops, not only on "Refresh".
   */
  const firstPreviewInputs = useRef(true);
  useEffect(() => {
    if (firstPreviewInputs.current) {
      firstPreviewInputs.current = false;
      return;
    }
    if (!approvalOpen) return;
    const timer = setTimeout(() => void loadPreview(approvalNote), 700);
    return () => clearTimeout(timer);
    // loadPreview is re-created every render; the inputs are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName, approvalNote]);

  // Nothing should outlive the component holding the object URL.
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/*
        The result, kept on screen.

        Every field below changes these three numbers, and a calculator that
        makes you scroll back up to find out what an edit did is one nobody
        checks their edits against. The panel is the sticky element itself
        rather than sitting inside a sticky wrapper: its own background is the
        themed one, so the inputs scrolling underneath stay hidden in both the
        light and the dark stylesheet without a hardcoded colour.
      */}
      <EuiPanel
        hasShadow
        paddingSize="m"
        style={{ position: "sticky", top: 0, zIndex: 2 }}
      >
        <EuiFlexGroup alignItems="center" gutterSize="m" wrap>
          <EuiFlexItem grow={false}>
            <EuiText size="xs" color="subdued">
              <strong style={{ textTransform: "uppercase", letterSpacing: "0.03em" }}>
                {translate(`${i18n}.contract.term`, {
                  defaultMessage: "Contract term",
                })}
              </strong>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButtonGroup
              legend={translate(`${i18n}.contract.termLegend`, {
                defaultMessage: "Select contract term",
              })}
              options={yearOptions}
              idSelected={contractYears.toString()}
              onChange={(id) => setContractYears(parseInt(id))}
              buttonSize="compressed"
            />
          </EuiFlexItem>
          <EuiFlexItem grow={true} />
          <EuiFlexItem grow={false}>
            <EuiSwitch
              label={translate(`${i18n}.contract.upfront`, {
                defaultMessage: "Upfront payment (additional discount)",
              })}
              checked={upfrontPayment}
              onChange={(e) => setUpfrontPayment(e.target.checked)}
              compressed
            />
          </EuiFlexItem>
        </EuiFlexGroup>

        {/*
          Say where the preselection came from. A term that is simply
          "selected" reads as a default; one that came from the agreed dates is
          a fact the operator can check - and override.
        */}
        {questionnaireTerm !== null && (
          <>
            <EuiSpacer size="xs" />
            <EuiText size="xs" color="subdued">
              {questionnaireTerm === contractYears
                ? translate(`${i18n}.contract.fromDates`, {
                    values: { term: yearLabel(questionnaireTerm) },
                    defaultMessage:
                      "Prefilled from the agreed contract dates ({{term}}).",
                  })
                : translate(`${i18n}.contract.overridden`, {
                    values: { term: yearLabel(questionnaireTerm) },
                    defaultMessage:
                      "The questionnaire's contract dates give {{term}}.",
                  })}
            </EuiText>
          </>
        )}

        <EuiHorizontalRule margin="s" />

        <EuiFlexGroup alignItems="flexEnd" gutterSize="l" wrap>
          <EuiFlexItem grow={false}>
            <EuiStat
              title={`€${euro(calculations.offeredMonthly)}`}
              description={
                uplifted
                  ? translate(`${i18n}.summary.monthlyWished`, {
                      defaultMessage: "Monthly (asking price)",
                    })
                  : translate(`${i18n}.summary.monthly`, {
                      defaultMessage: "Monthly",
                    })
              }
              titleSize="m"
              titleColor="success"
              reverse
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiStat
              title={`€${euro(calculations.offeredYearly)}`}
              description={translate(`${i18n}.summary.yearly`, {
                defaultMessage: "Yearly",
              })}
              titleSize="m"
              reverse
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiStat
              title={`€${euro(calculations.offeredContract)}`}
              description={translate(`${i18n}.summary.totalWithTerm`, {
                values: { term: yearLabel(contractYears) },
                defaultMessage: "Total ({{term}})",
              })}
              titleSize="m"
              reverse
            />
          </EuiFlexItem>
          {/*
            The customer offered more than calculated, and the offer quotes
            their price - so the calculated rate and the uplift are stated
            right under it rather than left to be worked out.
          */}
          {uplifted && (
            <EuiFlexItem grow={false}>
              <EuiBadge color="success" iconType="sortUp">
                {translate(`${i18n}.summary.uplift`, {
                  values: {
                    quoted: euro(calculations.finalMonthlyCost),
                    uplift: euro(calculations.upliftPerMonth),
                  },
                  defaultMessage: "calculated €{{quoted}} + €{{uplift}} uplift",
                })}
              </EuiBadge>
            </EuiFlexItem>
          )}
          <EuiFlexItem grow={true} />

          {/*
            How the customer's asking price stands against what the appliance
            costs. A badge and one sentence rather than the callout this
            replaces: a callout in a bar that is always on screen spends more
            height than the judgement is worth.
          */}
          {hasWishedPrice && (
            <EuiFlexItem grow={false}>
              <EuiFlexGroup
                direction="column"
                gutterSize="xs"
                alignItems="flexEnd"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  <EuiBadge
                    color={DEAL_COLOR[calculations.deal.rating]}
                    iconType={DEAL_ICON[calculations.deal.rating]}
                  >
                    {translate(`${i18n}.deal.${calculations.deal.rating}`, {
                      values: { margin: calculations.deal.marginPercent },
                      defaultMessage: "{{margin}}% margin",
                    })}
                  </EuiBadge>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="xs" color="subdued">
                    {translate(`${i18n}.deal.contribution`, {
                      values: {
                        amount: euro(
                          Math.abs(calculations.deal.contributionPerMonth),
                        ),
                        target: config.profitMargin,
                      },
                      defaultMessage:
                        "€{{amount}} per month over the delivery cost; the target is {{target}}%.",
                    })}
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>

        <EuiHorizontalRule margin="s" />

        <EuiFlexGroup alignItems="center" gutterSize="s" wrap>
          <EuiFlexItem grow={false}>
            <EuiText size="s" color="subdued">
              {translate(`${i18n}.field.wishedPrice`, {
                defaultMessage: "Customer's asking price (€)",
              })}
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false} style={{ minWidth: 170 }}>
            <EuiFieldNumber
              aria-label={translate(`${i18n}.field.wishedPrice`, {
                defaultMessage: "Customer's asking price (€)",
              })}
              value={config.wishedPrice}
              min={0}
              step={50}
              onChange={(e) =>
                updateConfig("wishedPrice", parseFloat(e.target.value) || 0)
              }
              prepend="€"
              // The unit the customer named it in. Stated rather than assumed:
              // reading a yearly budget as a monthly fee is an
              // order-of-magnitude slip, and nothing downstream would catch it.
              append={
                <EuiSelect
                  aria-label={translate(`${i18n}.field.wishedPriceBasis`, {
                    defaultMessage: "Price basis",
                  })}
                  value={config.wishedPriceBasis}
                  options={WISHED_PRICE_BASES.map((basis) => ({
                    value: basis,
                    text: translate(`${i18n}.basis.${basis}`, {
                      defaultMessage: basis,
                    }),
                  }))}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      wishedPriceBasis: e.target.value as WishedPriceBasis,
                    }))
                  }
                  compressed
                />
              }
              compressed
            />
          </EuiFlexItem>
          <EuiFlexItem grow={true}>
            <EuiText size="xs" color="subdued">
              {!hasWishedPrice
                ? translate(`${i18n}.deal.help`, {
                    defaultMessage:
                      "Leave at 0 until the customer has named a figure.",
                  })
                : calculations.deal.vsQuotedPerMonth < 0
                  ? translate(`${i18n}.deal.below`, {
                      values: {
                        amount: euro(
                          Math.abs(calculations.deal.vsQuotedPerMonth),
                        ),
                        term: yearLabel(contractYears),
                        total: euro(calculations.deal.termTotal),
                      },
                      defaultMessage:
                        "€{{amount}} below the calculated price - €{{total}} over {{term}}.",
                    })
                  : translate(`${i18n}.deal.above`, {
                      values: {
                        amount: euro(calculations.deal.vsQuotedPerMonth),
                        term: yearLabel(contractYears),
                        total: euro(calculations.deal.termTotal),
                      },
                      defaultMessage:
                        "€{{amount}} above the calculated price - €{{total}} over {{term}}.",
                    })}
            </EuiText>
          </EuiFlexItem>
          {/*
            What the customer's figure means per month, whenever they named it
            in anything but months. The conversion is the step an
            order-of-magnitude slip hides in, so it is shown rather than left to
            be trusted.
          */}
          {hasWishedPrice && config.wishedPriceBasis !== "monthly" && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.deal.equivalent`, {
                  values: { monthly: euro(calculations.wishedMonthly) },
                  defaultMessage: "That is €{{monthly}} per month.",
                })}
              </EuiText>
            </EuiFlexItem>
          )}

          {/*
            The action sits next to the indicator it acts on, so the figure
            being approved and the button approving it are never read apart.
          */}
          {canRequestApproval && (
          <EuiFlexItem grow={false}>
            <EuiButton
              fill
              // Warned about, never withheld: a deliberate loss-leader is still
              // a decision somebody is entitled to put forward.
              color={lossMaking ? "warning" : "primary"}
              size="s"
              iconType="documentEdit"
              onClick={openApproval}
            >
              {translate(`${i18n}.action.requestApproval`, {
                defaultMessage: "Request approval",
              })}
            </EuiButton>
          </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiPanel>

      {/*
        The document itself, before it leaves the building. An approval request
        is read by someone senior and carries a price; "send and hope" is the
        wrong interaction for it, so the operator sees the exact PDF the
        approver will get and can still add a justification.
      */}
      {approvalOpen && (
        <EuiModal onClose={closeApproval} maxWidth={980}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <h2>
                {translate(`${i18n}.approval.title`, {
                  values: { customer: customerName },
                  defaultMessage: "Request approval - {{customer}}",
                })}
              </h2>
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            {lossMaking && (
              <>
                <EuiCallOut
                  size="s"
                  color="warning"
                  iconType="alert"
                  title={translate(`${i18n}.approval.lossTitle`, {
                    defaultMessage: "This price is below the delivery cost",
                  })}
                >
                  <EuiText size="xs">
                    <p>
                      {translate(`${i18n}.approval.lossBody`, {
                        values: {
                          amount: euro(
                            Math.abs(calculations.deal.contributionPerMonth),
                          ),
                          term: yearLabel(contractYears),
                        },
                        defaultMessage:
                          "Every month of this contract loses €{{amount}} over {{term}}. The request can still be sent - the offer states the shortfall and its risks - but say here why it is worth it.",
                      })}
                    </p>
                  </EuiText>
                </EuiCallOut>
                <EuiSpacer size="s" />
              </>
            )}
            <EuiFormRow
              label={translate(`${i18n}.approval.customer`, {
                defaultMessage: "Customer (appears on the offer)",
              })}
              helpText={translate(`${i18n}.approval.customerHelp`, {
                defaultMessage:
                  "Prefilled from the appliance name - the questionnaire carries no customer field. Correct it before sending; the documentation tab and the attached documentation use the same name.",
              })}
              fullWidth
            >
              <EuiFieldText
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                fullWidth
              />
            </EuiFormRow>
            <EuiFormRow
              label={translate(`${i18n}.approval.note`, {
                defaultMessage: "Justification (appears in the offer)",
              })}
              helpText={translate(`${i18n}.approval.noteHelp`, {
                defaultMessage:
                  "The preview follows as you type.",
              })}
              fullWidth
            >
              <EuiTextArea
                value={approvalNote}
                onChange={(e) => setApprovalNote(e.target.value)}
                rows={2}
                fullWidth
              />
            </EuiFormRow>
            <EuiSpacer size="s" />
            <EuiFlexGroup gutterSize="s" alignItems="center" wrap>
              <EuiFlexItem grow={false}>
                {/* Both documents are attached; this picks the one on screen. */}
                <EuiButtonGroup
                  legend={translate(`${i18n}.approval.versionLegend`, {
                    defaultMessage: "Document to preview",
                  })}
                  options={[
                    {
                      id: "internal",
                      label: translate(`${i18n}.approval.versionInternal`, {
                        defaultMessage: "Approval document (internal)",
                      }),
                    },
                    {
                      id: "customer",
                      label: translate(`${i18n}.approval.versionCustomer`, {
                        defaultMessage: "Customer version",
                      }),
                    },
                  ]}
                  idSelected={previewVersion}
                  onChange={(id) => {
                    const version = id as "internal" | "customer";
                    setPreviewVersion(version);
                    void loadPreview(approvalNote, version);
                  }}
                  buttonSize="compressed"
                />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  size="s"
                  iconType="refresh"
                  isLoading={previewing}
                  onClick={() => void loadPreview(approvalNote)}
                >
                  {translate(`${i18n}.approval.refresh`, {
                    defaultMessage: "Refresh preview",
                  })}
                </EuiButtonEmpty>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size="s" />

            {/*
              Every addressee, named. An approval request carries a price and
              reaches people who did not raise it; "who sees this?" must be
              answerable before it is sent, not after.
            */}
            {recipients && !recipients.configured ? (
              <EuiCallOut
                size="s"
                color="danger"
                iconType="alert"
                title={translate(`${i18n}.approval.noRecipient`, {
                  defaultMessage:
                    "No approval mailbox is configured - the request cannot be sent",
                })}
              />
            ) : (
              <EuiPanel hasBorder hasShadow={false} paddingSize="s">
                <EuiFlexGroup gutterSize="s" alignItems="center" wrap>
                  <EuiFlexItem grow={false}>
                    <EuiText size="xs" color="subdued">
                      <strong>
                        {translate(`${i18n}.approval.to`, {
                          defaultMessage: "To",
                        })}
                      </strong>
                    </EuiText>
                  </EuiFlexItem>
                  {/*
                    One badge per address rather than a joined string: a list of
                    approvers is read, not skimmed, and a comma between two
                    addresses is easy to miss.
                  */}
                  {recipients ? (
                    recipients.recipients.map((address) => (
                      <EuiFlexItem grow={false} key={address}>
                        <EuiBadge color="primary">{address}</EuiBadge>
                      </EuiFlexItem>
                    ))
                  ) : recipientsFailed ? (
                    <EuiFlexItem grow={false}>
                      <EuiText size="xs" color="danger">
                        {translate(`${i18n}.approval.recipientsFailed`, {
                          defaultMessage: "Recipients could not be loaded",
                        })}
                      </EuiText>
                    </EuiFlexItem>
                  ) : (
                    <EuiFlexItem grow={false}>
                      <EuiLoadingSpinner size="s" />
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
                <EuiSpacer size="xs" />
                <EuiText size="xs" color="subdued">
                  {(recipients?.recipients.length ?? 0) > 1
                    ? translate(`${i18n}.approval.onlyRecipients`, {
                        values: { count: recipients?.recipients.length ?? 0 },
                        defaultMessage:
                          "This request goes to those {{count}} mailboxes alone - nobody else is copied.",
                      })
                    : translate(`${i18n}.approval.onlyRecipient`, {
                        defaultMessage:
                          "This request goes to that mailbox alone - nobody is copied.",
                      })}{" "}
                  {translate(`${i18n}.approval.withAttachment`, {
                    defaultMessage:
                      "Attached: the approval document, the customer version (which goes to the customer once the offer is approved) and the appliance documentation as PDF.",
                  })}
                </EuiText>
              </EuiPanel>
            )}
            <EuiSpacer size="s" />
            {previewing && !previewUrl ? (
              <EuiFlexGroup
                justifyContent="center"
                alignItems="center"
                style={{ height: 420 }}
              >
                <EuiFlexItem grow={false}>
                  <EuiLoadingSpinner size="xl" />
                </EuiFlexItem>
              </EuiFlexGroup>
            ) : previewUrl ? (
              <iframe
                title={translate(`${i18n}.approval.preview`, {
                  defaultMessage: "Offer preview",
                })}
                src={previewUrl}
                style={{
                  width: "100%",
                  height: 480,
                  border: "1px solid #d3dae6",
                  borderRadius: 4,
                }}
              />
            ) : (
              <EuiCallOut
                size="s"
                color="danger"
                iconType="alert"
                title={translate(`${i18n}.approval.previewFailed`, {
                  defaultMessage: "Could not render the offer",
                })}
              />
            )}
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButtonEmpty onClick={closeApproval}>
              {translate(`${i18n}.approval.cancel`, {
                defaultMessage: "Cancel",
              })}
            </EuiButtonEmpty>
            {/*
              A copy for the requester. The same blob the preview rendered and
              the same document the mail attaches, so what goes for approval and
              what is filed here cannot drift apart.
            */}
            <EuiButton
              iconType="download"
              href={previewUrl ?? undefined}
              download={offerFileName}
              isDisabled={!previewUrl || previewing}
            >
              {translate(`${i18n}.approval.download`, {
                defaultMessage: "Download PDF",
              })}
            </EuiButton>
            <EuiButton
              fill
              iconType="email"
              // Nothing is sent until a document has actually rendered: a mail
              // whose attachment failed to build is worse than no mail.
              isDisabled={
                !previewUrl ||
                previewing ||
                recipients?.configured === false ||
                customerName.trim() === ""
              }
              isLoading={sending}
              onClick={() => void sendApproval()}
            >
              {translate(`${i18n}.approval.send`, {
                defaultMessage: "Send request",
              })}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}

      <EuiSpacer size="m" />

      {/* ------------------------------------------------------------------ */}
      {/* Hardware                                                            */}
      {/* ------------------------------------------------------------------ */}
      <CostGroup
        id="cost-group-hardware"
        initialIsOpen
        title={translate(`${i18n}.hardware.title`, {
          defaultMessage: "Hardware Costs",
        })}
        note={translate(`${i18n}.group.hardware.total`, {
          defaultMessage: "Hardware less PoC credit",
        })}
        figure={`€${euro(hardwareCosts.baseTotal)}`}
      >
        {/*
          Where the prices came from, and on what basis. A quotation built on
          the standard build rather than on assigned hardware is a different
          statement, and so is a gross price - neither should have to be
          inferred from the numbers.
        */}
        {costBasis && (
          <>
            <EuiCallOut
              size="s"
              color={
                costBasis.some((line) => !line.fullyPriced)
                  ? "warning"
                  : "primary"
              }
              iconType="tag"
              title={
                costBasis.every((line) => line.source === "default")
                  ? translate(`${i18n}.basis.default`, {
                      defaultMessage:
                        "No hardware assigned yet - priced as the standard build (2 servers, 1 switch, 5 DAC cables).",
                    })
                  : translate(`${i18n}.basis.assigned`, {
                      defaultMessage:
                        "Prices from the assigned NetBox devices (purchase_price on the device type).",
                    })
              }
            >
              <EuiText size="xs">
                <p>
                  {translate(`${i18n}.basis.vat`, {
                    defaultMessage:
                      "All NetBox prices include sales tax. Every field stays editable.",
                  })}
                </p>
                {costBasis
                  .filter((line) => !line.fullyPriced)
                  .map((line) => (
                    <p key={line.kind}>
                      {translate(`${i18n}.basis.unpriced`, {
                        values: { kind: line.kind },
                        defaultMessage:
                          "{{kind}}: at least one device type carries no purchase_price in NetBox - that line is not fully priced.",
                      })}
                    </p>
                  ))}
              </EuiText>
            </EuiCallOut>
            <EuiSpacer size="m" />
          </>
        )}

        {config.hardware.server && (
          <>
            <SubGroup
              label={translate(`${i18n}.hardware.server`, {
                defaultMessage: "Server",
              })}
            />
            <EuiFlexGrid columns={2} gutterSize="m">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.model`, {
                    defaultMessage: "Model",
                  })}
                  fullWidth
                >
                  <EuiFieldText
                    value={config.hardware.server.model}
                    onChange={(e) =>
                      updateHardware("server", "model", e.target.value)
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.serverCount`, {
                    defaultMessage: "Number of servers",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.server.count}
                    onChange={(e) =>
                      updateHardware(
                        "server",
                        "count",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.costPerServer`, {
                    defaultMessage: "Cost per server (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.server.cost}
                    onChange={(e) =>
                      updateHardware(
                        "server",
                        "cost",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    prepend="€"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.hardware.row.subtotal`, {
                    defaultMessage: "Line total",
                  })}
                  fullWidth
                >
                  <EuiText size="s" style={{ lineHeight: "40px" }}>
                    <strong>€{euro(hardwareCosts.serverTotal)}</strong>
                  </EuiText>
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGrid>
            <EuiHorizontalRule margin="m" />
          </>
        )}

        {config.hardware.switch && (
          <>
            <SubGroup
              label={translate(`${i18n}.hardware.switch`, {
                defaultMessage: "Switch",
              })}
            />
            <EuiFlexGrid columns={2} gutterSize="m">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.model`, {
                    defaultMessage: "Model",
                  })}
                  fullWidth
                >
                  <EuiFieldText
                    value={config.hardware.switch.model}
                    onChange={(e) =>
                      updateHardware("switch", "model", e.target.value)
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.switchCount`, {
                    defaultMessage: "Number of switches",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.switch.count}
                    onChange={(e) =>
                      updateHardware(
                        "switch",
                        "count",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.costPerSwitch`, {
                    defaultMessage: "Cost per switch (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.switch.cost}
                    onChange={(e) =>
                      updateHardware(
                        "switch",
                        "cost",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    prepend="€"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.hardware.row.subtotal`, {
                    defaultMessage: "Line total",
                  })}
                  fullWidth
                >
                  <EuiText size="s" style={{ lineHeight: "40px" }}>
                    <strong>€{euro(hardwareCosts.switchTotal)}</strong>
                  </EuiText>
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGrid>
            <EuiHorizontalRule margin="m" />
          </>
        )}

        {config.hardware.probe && (
          <>
            <SubGroup
              label={translate(`${i18n}.hardware.probe`, {
                defaultMessage: "Probes",
              })}
            />
            <EuiFlexGrid columns={2} gutterSize="m">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.model`, {
                    defaultMessage: "Model",
                  })}
                  fullWidth
                >
                  <EuiFieldText
                    value={config.hardware.probe.model}
                    onChange={(e) =>
                      updateHardware("probe", "model", e.target.value)
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.probeCount`, {
                    defaultMessage: "Number of probes",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.probe.count}
                    onChange={(e) =>
                      updateHardware(
                        "probe",
                        "count",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.costPerProbe`, {
                    defaultMessage: "Cost per probe (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.probe.cost}
                    onChange={(e) =>
                      updateHardware(
                        "probe",
                        "cost",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    prepend="€"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.hardware.row.subtotal`, {
                    defaultMessage: "Line total",
                  })}
                  fullWidth
                >
                  <EuiText size="s" style={{ lineHeight: "40px" }}>
                    <strong>€{euro(hardwareCosts.probeTotal)}</strong>
                  </EuiText>
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGrid>
            <EuiHorizontalRule margin="m" />
          </>
        )}

        {config.hardware.cable && (
          <>
            <SubGroup
              label={translate(`${i18n}.hardware.cable`, {
                defaultMessage: "Cables & Transceivers",
              })}
            />
            <EuiFlexGrid columns={2} gutterSize="m">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.patchCount`, {
                    defaultMessage: "Number of patch cables",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.cable.patch.count}
                    onChange={(e) =>
                      updateHardware(
                        "cable",
                        "patch.count",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.patchCost`, {
                    defaultMessage: "Cost per patch cable (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.cable.patch.cost}
                    onChange={(e) =>
                      updateHardware(
                        "cable",
                        "patch.cost",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    prepend="€"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.transceiverCount`, {
                    defaultMessage: "Number of transceivers",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.cable.transceiver.count}
                    onChange={(e) =>
                      updateHardware(
                        "cable",
                        "transceiver.count",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.transceiverCost`, {
                    defaultMessage: "Cost per transceiver (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.cable.transceiver.cost}
                    onChange={(e) =>
                      updateHardware(
                        "cable",
                        "transceiver.cost",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    prepend="€"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.dac.title`, {
                    defaultMessage: "DAC cables",
                  })}
                  fullWidth
                >
                  <EuiFieldText
                    value={config.hardware.cable.dac.model}
                    onChange={(e) =>
                      updateHardware("cable", "dac.model", e.target.value)
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.dacCount`, {
                    defaultMessage: "Number of DAC cables",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.cable.dac.count}
                    onChange={(e) =>
                      updateHardware(
                        "cable",
                        "dac.count",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.field.dacCost`, {
                    defaultMessage: "Cost per DAC cable (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.hardware.cable.dac.cost}
                    onChange={(e) =>
                      updateHardware(
                        "cable",
                        "dac.cost",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    prepend="€"
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.hardware.row.subtotal`, {
                    defaultMessage: "Line total",
                  })}
                  fullWidth
                >
                  <EuiText size="s" style={{ lineHeight: "40px" }}>
                    <strong>€{euro(hardwareCosts.cableTotal)}</strong>
                  </EuiText>
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGrid>

            {/* The three cable lines, as one text row rather than a panel. */}
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
              {translate(`${i18n}.cable.patch.summary`, {
                values: { cost: euro(hardwareCosts.patchCableTotal) },
                defaultMessage: "Patch cables: €{{cost}}",
              })}
              {" · "}
              {translate(`${i18n}.transceiver.summary`, {
                values: { cost: euro(hardwareCosts.transceiverTotal) },
                defaultMessage: "Transceivers: €{{cost}}",
              })}
              {" · "}
              {translate(`${i18n}.dac.summary`, {
                values: { cost: euro(hardwareCosts.dacTotal) },
                defaultMessage: "DAC cables: €{{cost}}",
              })}
            </EuiText>
            <EuiHorizontalRule margin="m" />
          </>
        )}

        {/*
          The PoC credit. Prefilled from the questionnaire's own answer and
          editable, because whether a given PoC is actually credited - and at
          what - is a commercial decision the questionnaire does not make.
        */}
        <EuiFlexGrid columns={2} gutterSize="m">
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.pocOffset`, {
                defaultMessage: "PoC credit (€)",
              })}
              helpText={
                wantPoc
                  ? translate(`${i18n}.poc.fromQuestionnaire`, {
                      defaultMessage:
                        "The questionnaire records a proof of concept; it is credited once against the hardware. Set to 0 to waive it.",
                    })
                  : translate(`${i18n}.poc.none`, {
                      defaultMessage:
                        "The questionnaire records no proof of concept. Enter an amount to credit one anyway.",
                    })
              }
              fullWidth
            >
              <EuiFieldNumber
                value={config.pocOffset}
                min={0}
                step={100}
                onChange={(e) =>
                  updateConfig("pocOffset", parseFloat(e.target.value) || 0)
                }
                prepend="€"
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.distributorMargin`, {
                values: { percent: config.distributorMarginPercent },
                defaultMessage: "Distributor margin ({{percent}}%)",
              })}
              fullWidth
            >
              <EuiRange
                min={0}
                max={30}
                step={0.5}
                value={config.distributorMarginPercent}
                onChange={(e) =>
                  updateConfig(
                    "distributorMarginPercent",
                    parseFloat(e.currentTarget.value),
                  )
                }
                showValue
                append="%"
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
        </EuiFlexGrid>

        {/*
          What the group actually comes to. Plain text above a rule rather than
          a filled panel inside a filled panel - the body carries one level of
          panel and derived figures are text.
        */}
        <EuiHorizontalRule margin="m" />
        <EuiText size="xs" color="subdued">
          {hardwareCosts.pocOffset > 0 && (
            <p>
              {translate(`${i18n}.hardware.gross`, {
                defaultMessage: "Hardware before PoC credit:",
              })}{" "}
              €{euro(hardwareCosts.grossTotal)}
              {" · "}
              {translate(`${i18n}.hardware.pocOffset`, {
                defaultMessage: "PoC credit:",
              })}{" "}
              -€{euro(hardwareCosts.pocOffset)}
            </p>
          )}
          <p>
            {translate(`${i18n}.hardware.withMargin`, {
              values: { cost: euro(calculations.hardwareCostWithMargin) },
              defaultMessage: "Hardware incl. margin: €{{cost}}",
            })}
          </p>
          {locationSizing.sites > 0 && (
            <p>
              {translate(`${i18n}.sizing.source`, {
                defaultMessage:
                  "Source: installation site → address. Server, switch and DAC-cable counts are prefilled from the appliance's selected NetBox devices (and the model is taken from NetBox); the probe count is prefilled from the configured customer-site probes; the switch count falls back to the number of sites when no NetBox switch is selected. All counts remain editable.",
              })}
            </p>
          )}
        </EuiText>
      </CostGroup>

      <EuiSpacer size="s" />

      {/* ------------------------------------------------------------------ */}
      {/* Services                                                            */}
      {/* ------------------------------------------------------------------ */}
      <CostGroup
        id="cost-group-services"
        title={translate(`${i18n}.services.title`, {
          defaultMessage: "Services",
        })}
        note={translate(`${i18n}.group.services.badge`, {
          values: {
            bundle: bundleLabel(calculations.serviceBundle),
            coverage: coverageLabel(calculations.coverageTier),
            hours: calculations.effortHours.total,
            rate: config.hourlyRate,
          },
          defaultMessage:
            "{{bundle}} · {{coverage}} · {{hours}} h/month · €{{rate}}/h",
        })}
        figure={`€${euro(
          calculations.monthlyOperatingCost +
            calculations.coverageFee +
            calculations.outOfHours.cost +
            calculations.onSite.perMonth +
            calculations.customDevelopment.perMonth +
            calculations.updateDelivery.perMonth,
        )}`}
      >
        {/*
          The bundle picks which services are included; each service keeps its
          own hours. Ticking a combination no bundle names is "custom" - the
          bundle is read off the services, never stored beside them, so the
          offer cannot name a bundle the price was not built from.
        */}
        <SubGroup
          label={translate(`${i18n}.services.bundle`, {
            defaultMessage: "Service bundle",
          })}
        />
        <EuiFlexGroup alignItems="center" gutterSize="s" wrap responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiButtonGroup
              legend={translate(`${i18n}.services.bundleLegend`, {
                defaultMessage: "Select service bundle",
              })}
              options={SERVICE_BUNDLE_IDS.filter((b) => b !== "custom").map(
                (bundle) => ({ id: bundle, label: bundleLabel(bundle) }),
              )}
              // "custom" has no button: it is where ticking your own
              // combination lands, not something to switch to.
              idSelected={calculations.serviceBundle}
              onChange={(id) => selectServiceBundle(id as ServiceBundleId)}
              buttonSize="compressed"
            />
          </EuiFlexItem>
          {calculations.serviceBundle === "custom" ? (
            <EuiFlexItem grow={false}>
              <EuiBadge color="hollow">{bundleLabel("custom")}</EuiBadge>
            </EuiFlexItem>
          ) : (
            <EuiFlexItem grow={false} style={{ minWidth: 200 }}>
              <EuiFieldNumber
                aria-label={translate(`${i18n}.services.bundleFee`, {
                  values: { bundle: bundleLabel(calculations.serviceBundle) },
                  defaultMessage: "Fixed fee {{bundle}} (€ / month)",
                })}
                value={
                  config.effort.bundleFees[
                    calculations.serviceBundle as NamedServiceBundle
                  ]
                }
                min={0}
                step={50}
                onChange={(e) =>
                  updateBundleFee(
                    calculations.serviceBundle as NamedServiceBundle,
                    parseFloat(e.target.value) || 0,
                  )
                }
                prepend="€"
                append={translate(`${i18n}.services.perMonth`, {
                  defaultMessage: "/ month",
                })}
                compressed
              />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
        <EuiSpacer size="xs" />
        {/*
          A fixed fee hides whether it still covers the hours behind it, so the
          effort value sits right next to it - and turns amber when the fee
          falls short.
        */}
        <EuiText
          size="xs"
          color={
            calculations.bundleFee !== null &&
            calculations.bundleFee < calculations.serviceEffortCost
              ? "warning"
              : "subdued"
          }
        >
          {calculations.bundleFee !== null
            ? translate(`${i18n}.services.feeVsEffort`, {
                values: {
                  bundle: bundleLabel(calculations.serviceBundle),
                  effort: euro(calculations.serviceEffortCost),
                  hours: calculations.effortHours.total,
                },
                defaultMessage:
                  "{{bundle}} is billed at its fixed fee. By effort ({{hours}} h) it would be €{{effort}} per month.",
              })
            : translate(`${i18n}.services.customByEffort`, {
                defaultMessage:
                  "Custom combination: billed by effort (hours × hourly rate).",
              })}
        </EuiText>
        <EuiSpacer size="xs" />
        <EuiText size="xs" color="subdued">
          {translate(`${i18n}.services.bundleHelp`, {
            defaultMessage:
              "Basic: Update Service and Maintenance · Standard: plus Support · Premium: plus Weeklys. Services left out are billed separately by effort.",
          })}
        </EuiText>
        <EuiSpacer size="m" />

        {SERVICE_IDS.map((id) => {
          const line = config.effort.services[id];
          return (
            <React.Fragment key={id}>
              <EuiFlexGroup alignItems="center" gutterSize="m" wrap>
                <EuiFlexItem grow={2} style={{ minWidth: 220 }}>
                  <EuiSwitch
                    label={
                      <>
                        <strong>{serviceLabel(id)}</strong>
                        <EuiText size="xs" color="subdued">
                          {translate(`${i18n}.service.${id}.description`, {
                            defaultMessage: "",
                          })}
                        </EuiText>
                      </>
                    }
                    checked={line.included}
                    onChange={(e) =>
                      updateService(id, { included: e.target.checked })
                    }
                    compressed
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={1} style={{ minWidth: 200 }}>
                  <EuiFieldNumber
                    aria-label={translate(`${i18n}.services.hours`, {
                      values: { service: serviceLabel(id) },
                      defaultMessage: "{{service}} effort in hours",
                    })}
                    value={line.hours}
                    min={0}
                    step={0.25}
                    disabled={!line.included}
                    onChange={(e) =>
                      updateService(id, {
                        hours: parseFloat(e.target.value) || 0,
                      })
                    }
                    append={
                      <EuiSelect
                        aria-label={translate(`${i18n}.services.per`, {
                          defaultMessage: "Counted per",
                        })}
                        value={line.per}
                        options={(["month", "week"] as ServiceUnit[]).map(
                          (unit) => ({
                            value: unit,
                            text: translate(`${i18n}.services.per.${unit}`, {
                              defaultMessage: `h / ${unit}`,
                            }),
                          }),
                        )}
                        disabled={!line.included}
                        onChange={(e) =>
                          updateService(id, {
                            per: e.target.value as ServiceUnit,
                          })
                        }
                        compressed
                      />
                    }
                    compressed
                    fullWidth
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false} style={{ minWidth: 110, textAlign: "right" }}>
                  <EuiText
                    size="s"
                    // Under a fixed bundle fee these are the effort behind it,
                    // not what is billed.
                    color={
                      line.included && calculations.bundleFee === null
                        ? "default"
                        : "subdued"
                    }
                  >
                    {line.included ? (
                      <strong>
                        €
                        {euro(
                          calculations.effortHours.services[id] *
                            config.hourlyRate,
                        )}
                      </strong>
                    ) : (
                      translate(`${i18n}.services.excluded`, {
                        defaultMessage: "not included",
                      })
                    )}
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size="s" />
            </React.Fragment>
          );
        })}

        {/*
          How updates reach the appliance. Online is what the Update Service
          hours already cover; a gateway and a dark site cost extra - once for
          the gateway, and on every single update for the USB drive.
        */}
        <EuiHorizontalRule margin="m" />
        <SubGroup
          label={translate(`${i18n}.updates.title`, {
            defaultMessage: "Update delivery",
          })}
        />
        <EuiButtonGroup
          legend={translate(`${i18n}.updates.legend`, {
            defaultMessage: "Select how updates reach the appliance",
          })}
          options={UPDATE_METHODS.map((method) => ({
            id: method,
            label: translate(`${i18n}.updates.${method}.title`, {
              defaultMessage: method,
            }),
          }))}
          idSelected={config.updateDelivery.method}
          onChange={(id) => selectUpdateMethod(id as UpdateMethodId)}
          buttonSize="compressed"
        />
        <EuiSpacer size="xs" />
        <EuiText size="xs" color="subdued">
          {translate(
            `${i18n}.updates.${config.updateDelivery.method}.description`,
            { defaultMessage: "" },
          )}
        </EuiText>
        {!calculations.updateDelivery.applies ? (
          <>
            <EuiSpacer size="xs" />
            <EuiText size="xs" color="warning">
              {translate(`${i18n}.updates.notIncluded`, {
                defaultMessage:
                  "The Update Service is not included - no updates are delivered, so the delivery method is not charged.",
              })}
            </EuiText>
          </>
        ) : (
          config.updateDelivery.method !== "online" && (
            <>
              <EuiSpacer size="s" />
              <EuiFlexGrid columns={4} gutterSize="s">
                <EuiFlexItem>
                  <EuiFormRow
                    label={translate(`${i18n}.updates.perYear`, {
                      defaultMessage: "Updates per year",
                    })}
                    fullWidth
                  >
                    <EuiFieldNumber
                      value={config.updateDelivery.updatesPerYear}
                      min={0}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          updateDelivery: {
                            ...prev.updateDelivery,
                            updatesPerYear: parseFloat(e.target.value) || 0,
                          },
                        }))
                      }
                      compressed
                      fullWidth
                    />
                  </EuiFormRow>
                </EuiFlexItem>
                {config.updateDelivery.method === "gateway" ? (
                  <>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate(`${i18n}.updates.gateway.hardwareCost`, {
                        defaultMessage: "Gateway system, one-off (€)",
                      })}
                      fullWidth
                    >
                      <EuiFieldNumber
                        value={config.updateDelivery.gateway.hardwareCost}
                        min={0}
                        step={100}
                        onChange={(e) =>
                          updateDeliveryField(
                            "gateway",
                            "hardwareCost",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        prepend="€"
                        compressed
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate(`${i18n}.updates.gateway.setupHours`, {
                        defaultMessage: "Gateway setup, one-off",
                      })}
                      fullWidth
                    >
                      <EuiFieldNumber
                        value={config.updateDelivery.gateway.setupHours}
                        min={0}
                        step={1}
                        onChange={(e) =>
                          updateDeliveryField(
                            "gateway",
                            "setupHours",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        append="h"
                        compressed
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate(`${i18n}.updates.gateway.hoursPerUpdate`, {
                        defaultMessage: "Staging per update",
                      })}
                      fullWidth
                    >
                      <EuiFieldNumber
                        value={config.updateDelivery.gateway.hoursPerUpdate}
                        min={0}
                        step={0.25}
                        onChange={(e) =>
                          updateDeliveryField(
                            "gateway",
                            "hoursPerUpdate",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        append="h"
                        compressed
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  </>
                ) : (
                  <>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate(`${i18n}.updates.airgap.hoursPerUpdate`, {
                        defaultMessage: "Prepare & verify per delivery",
                      })}
                      fullWidth
                    >
                      <EuiFieldNumber
                        value={config.updateDelivery.airgap.hoursPerUpdate}
                        min={0}
                        step={0.25}
                        onChange={(e) =>
                          updateDeliveryField(
                            "airgap",
                            "hoursPerUpdate",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        append="h"
                        compressed
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate(`${i18n}.updates.airgap.mediaCostPerUpdate`, {
                        defaultMessage: "Encrypted USB media per delivery (€)",
                      })}
                      fullWidth
                    >
                      <EuiFieldNumber
                        value={config.updateDelivery.airgap.mediaCostPerUpdate}
                        min={0}
                        step={5}
                        onChange={(e) =>
                          updateDeliveryField(
                            "airgap",
                            "mediaCostPerUpdate",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        prepend="€"
                        compressed
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate(`${i18n}.updates.airgap.shippingPerUpdate`, {
                        defaultMessage: "Tracked shipping per delivery (€)",
                      })}
                      fullWidth
                    >
                      <EuiFieldNumber
                        value={config.updateDelivery.airgap.shippingPerUpdate}
                        min={0}
                        step={5}
                        onChange={(e) =>
                          updateDeliveryField(
                            "airgap",
                            "shippingPerUpdate",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        prepend="€"
                        compressed
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  </>
                )}
              </EuiFlexGrid>
              <EuiSpacer size="s" />
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.updates.breakdown`, {
                  values: {
                    hours: calculations.updateDelivery.hoursPerYear,
                    perMonth: euro(calculations.updateDelivery.perMonth),
                    oneOff: euro(calculations.updateDelivery.oneOff),
                  },
                  defaultMessage:
                    "{{hours}} h a year = €{{perMonth}} per month; one-off €{{oneOff}}, recovered alongside the hardware.",
                })}
              </EuiText>
              {config.updateDelivery.method === "airgap" && (
                <>
                  <EuiSpacer size="s" />
                  <EuiCallOut
                    size="s"
                    color="warning"
                    iconType="alert"
                    title={translate(`${i18n}.updates.airgap.risk`, {
                      values: { count: config.updateDelivery.updatesPerYear },
                      defaultMessage:
                        "A dark site only gets updates with the next USB delivery ({{count}} per year). A critical vulnerability stays open until then unless an emergency delivery is ordered.",
                    })}
                  />
                </>
              )}
            </>
          )
        )}

        {/*
          Service hours: when the customer can reach us. A fixed fee per month
          rather than more hours - what a wider window costs is the standby
          behind it, not the work done in it.
        */}
        <EuiHorizontalRule margin="m" />
        <SubGroup
          label={translate(`${i18n}.coverage.title`, {
            defaultMessage: "Service hours",
          })}
        />
        <EuiFlexGroup alignItems="flexEnd" gutterSize="m" wrap>
          <EuiFlexItem grow={false}>
            <EuiFormRow
              label={translate(`${i18n}.coverage.package`, {
                defaultMessage: "Package",
              })}
            >
              <EuiButtonGroup
                legend={translate(`${i18n}.coverage.legend`, {
                  defaultMessage: "Select service hours",
                })}
                options={COVERAGE_TIERS.map((tier) => ({
                  id: tier,
                  label: coverageLabel(tier),
                }))}
                idSelected={config.coverage.tier}
                onChange={(id) => selectCoverage(id as CoverageTierId)}
                buttonSize="compressed"
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem grow={false} style={{ minWidth: 200 }}>
            <EuiFormRow
              label={translate(`${i18n}.coverage.fee`, {
                values: { tier: coverageLabel(config.coverage.tier) },
                defaultMessage: "Fixed fee {{tier}} (€ / month)",
              })}
            >
              <EuiFieldNumber
                value={config.coverage.monthlyFees[config.coverage.tier]}
                min={0}
                step={50}
                onChange={(e) =>
                  updateCoverageFee(
                    config.coverage.tier,
                    parseFloat(e.target.value) || 0,
                  )
                }
                prepend="€"
                compressed
              />
            </EuiFormRow>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="xs" />
        <EuiText size="xs" color="subdued">
          {translate(`${i18n}.coverage.${config.coverage.tier}.hours`, {
            defaultMessage: "",
          })}
        </EuiText>

        {/*
          Call-outs outside business hours, priced with the surcharges the
          hours they fall into carry. Only for a window that reaches outside
          business hours - business days have none.
        */}
        {coversOutOfHours && (
          <>
            <EuiSpacer size="m" />
            <EuiFlexGrid columns={2} gutterSize="m">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.outOfHours.hours`, {
                    defaultMessage:
                      "Expected call-outs outside business hours",
                  })}
                  helpText={translate(`${i18n}.outOfHours.hoursHelp`, {
                    defaultMessage:
                      "Outside the service hours Mon-Fri 9-17 (Ziffer 8.2). Assumed evenly spread over those hours.",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.coverage.outOfHoursHours}
                    min={0}
                    step={0.5}
                    onChange={(e) =>
                      updateCoverage(
                        "outOfHoursHours",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    append={translate(`${i18n}.services.per.month`, {
                      defaultMessage: "h / month",
                    })}
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.outOfHours.holidays`, {
                    defaultMessage: "Public holidays per year",
                  })}
                  helpText={translate(`${i18n}.outOfHours.holidaysHelp`, {
                    defaultMessage:
                      "The terms count the holidays at the Hamburg location (Ziffer 8.2): 10.",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.coverage.holidaysPerYear}
                    min={9}
                    max={20}
                    onChange={(e) =>
                      updateCoverage(
                        "holidaysPerYear",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGrid>
            <EuiSpacer size="s" />
            <EuiAccordion
              id="cost-surcharges"
              buttonContent={
                <EuiText size="s">
                  {translate(`${i18n}.surcharge.title`, {
                    values: {
                      percent: calculations.outOfHours.averageSurchargePercent,
                    },
                    defaultMessage:
                      "Surcharges (§3b EStG / §6 ArbZG) - average {{percent}}%",
                  })}
                </EuiText>
              }
              paddingSize="s"
            >
              <EuiFlexGrid columns={3} gutterSize="s">
                {(
                  [
                    "night",
                    "deepNight",
                    "sunday",
                    "holiday",
                    "special",
                  ] as (keyof SurchargeRates)[]
                ).map((field) => (
                  <EuiFlexItem key={field}>
                    <EuiFormRow
                      label={translate(`${i18n}.surcharge.${field}`, {
                        defaultMessage: field,
                      })}
                      fullWidth
                    >
                      <EuiFieldNumber
                        value={config.coverage.surcharges[field]}
                        min={0}
                        step={5}
                        onChange={(e) =>
                          updateSurcharge(field, parseFloat(e.target.value) || 0)
                        }
                        append="%"
                        compressed
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                ))}
              </EuiFlexGrid>
              <EuiSpacer size="s" />
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.surcharge.help`, {
                  defaultMessage:
                    "Defaults are the tax-free rates of §3b EStG. The law itself only requires an appropriate night-work surcharge (§6 Abs. 5 ArbZG, 25% per BAG); Sunday and holiday work require a replacement rest day instead. Night and Sunday/holiday surcharges add up.",
                })}
              </EuiText>
            </EuiAccordion>
            <EuiSpacer size="s" />
            {/*
              The working-time rules decide whether the window can be staffed at
              all, not only what it costs - so they are said here, where the
              window is chosen, and again in the offer.
            */}
            <EuiCallOut
              size="s"
              color="primary"
              iconType="iInCircle"
              title={translate(`${i18n}.arbzg.title`, {
                defaultMessage: "Working Hours Act (ArbZG)",
              })}
            >
              <EuiText size="xs">
                <ul>
                  <li>
                    {translate(`${i18n}.arbzg.rest`, {
                      defaultMessage:
                        "On-call standby is not working time, but every call-out is. After a call-out the 11-hour rest period starts again (§5 ArbZG) - staff the standby as a rotation of several people.",
                    })}
                  </li>
                  {config.coverage.tier === "24x7" && (
                    <>
                      <li>
                        {translate(`${i18n}.arbzg.night`, {
                          defaultMessage:
                            "Night work (23-6, §2 Abs. 3 ArbZG) requires an appropriate surcharge (§6 Abs. 5 ArbZG).",
                        })}
                      </li>
                      <li>
                        {translate(`${i18n}.arbzg.sunday`, {
                          defaultMessage:
                            "Sunday and holiday call-outs are only permitted under §10 ArbZG, require a replacement rest day (§11 Abs. 3) and leave each person at least 15 free Sundays a year (§11 Abs. 1).",
                        })}
                      </li>
                    </>
                  )}
                </ul>
              </EuiText>
            </EuiCallOut>
          </>
        )}

        {/*
          On-site support, an extension of the remote services. Priced by what
          a visit costs - time on site, travel time (working time), kilometres,
          nights away and the statutory meal allowance - because the same visit
          is a different thing 20 km and 400 km away.
        */}
        <EuiHorizontalRule margin="m" />
        <SubGroup
          label={translate(`${i18n}.onSite.title`, {
            defaultMessage: "On-site support (extension)",
          })}
        />
        <EuiSwitch
          label={translate(`${i18n}.onSite.include`, {
            defaultMessage: "Include on-site visits",
          })}
          checked={config.onSite.included}
          onChange={(e) => updateOnSite("included", e.target.checked)}
          compressed
        />
        {config.onSite.included && (
          <>
            <EuiSpacer size="s" />
            <EuiFlexGrid columns={4} gutterSize="s">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.visitsPerYear`, {
                    defaultMessage: "Visits per year",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.visitsPerYear}
                    min={0}
                    step={1}
                    onChange={(e) =>
                      updateOnSite("visitsPerYear", parseFloat(e.target.value) || 0)
                    }
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.hoursPerVisit`, {
                    defaultMessage: "Hours on site per visit",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.hoursPerVisit}
                    min={0}
                    step={0.5}
                    onChange={(e) =>
                      updateOnSite("hoursPerVisit", parseFloat(e.target.value) || 0)
                    }
                    append="h"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.distanceKm`, {
                    defaultMessage: "Distance to site (one way)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.distanceKm}
                    min={0}
                    step={5}
                    onChange={(e) =>
                      updateOnSite("distanceKm", parseFloat(e.target.value) || 0)
                    }
                    append="km"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.averageSpeedKmh`, {
                    defaultMessage: "Average travel speed",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.averageSpeedKmh}
                    min={0}
                    step={5}
                    onChange={(e) =>
                      updateOnSite("averageSpeedKmh", parseFloat(e.target.value) || 0)
                    }
                    append="km/h"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.overnightStays`, {
                    defaultMessage: "Overnight stays per visit",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.overnightStays}
                    min={0}
                    step={1}
                    onChange={(e) =>
                      updateOnSite("overnightStays", parseFloat(e.target.value) || 0)
                    }
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.hotelPerNight`, {
                    defaultMessage: "Hotel per night (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.hotelPerNight}
                    min={0}
                    step={10}
                    onChange={(e) =>
                      updateOnSite("hotelPerNight", parseFloat(e.target.value) || 0)
                    }
                    prepend="€"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.travelRatePercent`, {
                    defaultMessage: "Travel time billed at",
                  })}
                  helpText={translate(`${i18n}.onSite.help.travelRatePercent`, {
                    defaultMessage: "Only for the effort comparison below.",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.travelRatePercent}
                    min={0}
                    step={5}
                    onChange={(e) =>
                      updateOnSite("travelRatePercent", parseFloat(e.target.value) || 0)
                    }
                    append="%"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.onSite.field.costPerKm`, {
                    defaultMessage: "Cost per km (€)",
                  })}
                  helpText={translate(`${i18n}.onSite.help.costPerKm`, {
                    defaultMessage: "Only for the effort comparison below.",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.onSite.costPerKm}
                    min={0}
                    step={0.05}
                    onChange={(e) =>
                      updateOnSite("costPerKm", parseFloat(e.target.value) || 0)
                    }
                    prepend="€"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGrid>

            {/*
              The travel charge by distance zone. The one-way distance picks the
              zone; beyond the last zone every further kilometre is added. The
              active zone is marked so the charge can be traced to its row.
            */}
            <EuiSpacer size="m" />
            <SubGroup
              label={translate(`${i18n}.onSite.zones.title`, {
                defaultMessage: "Travel charge by distance (one way)",
              })}
            />
            {config.onSite.travelZones.map((zone, index) => (
              <React.Fragment key={index}>
                <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false} wrap>
                  <EuiFlexItem grow={false} style={{ minWidth: 170 }}>
                    <EuiFieldNumber
                      aria-label={translate(`${i18n}.onSite.zones.upTo`, {
                        defaultMessage: "Up to (km)",
                      })}
                      value={zone.upToKm}
                      min={0}
                      step={5}
                      onChange={(e) =>
                        updateOnSite(
                          "travelZones",
                          config.onSite.travelZones.map((z, i) =>
                            i === index
                              ? { ...z, upToKm: parseFloat(e.target.value) || 0 }
                              : z,
                          ),
                        )
                      }
                      prepend={translate(`${i18n}.onSite.zones.upToPrefix`, {
                        defaultMessage: "up to",
                      })}
                      append="km"
                      compressed
                    />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false} style={{ minWidth: 170 }}>
                    <EuiFieldNumber
                      aria-label={translate(`${i18n}.onSite.zones.flat`, {
                        defaultMessage: "Travel charge (€)",
                      })}
                      value={zone.flat}
                      min={0}
                      step={5}
                      onChange={(e) =>
                        updateOnSite(
                          "travelZones",
                          config.onSite.travelZones.map((z, i) =>
                            i === index
                              ? { ...z, flat: parseFloat(e.target.value) || 0 }
                              : z,
                          ),
                        )
                      }
                      prepend="€"
                      compressed
                    />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButtonEmpty
                      size="xs"
                      color="danger"
                      iconType="trash"
                      aria-label={translate(`${i18n}.onSite.zones.remove`, {
                        defaultMessage: "Remove zone",
                      })}
                      isDisabled={config.onSite.travelZones.length <= 1}
                      onClick={() =>
                        updateOnSite(
                          "travelZones",
                          config.onSite.travelZones.filter((_, i) => i !== index),
                        )
                      }
                    />
                  </EuiFlexItem>
                  {calculations.onSite.travel.zone >= 0 &&
                    zone.upToKm === calculations.onSite.travel.upToKm && (
                      <EuiFlexItem grow={false}>
                        <EuiBadge color="primary">
                          {translate(`${i18n}.onSite.zones.active`, {
                            defaultMessage: "applies",
                          })}
                        </EuiBadge>
                      </EuiFlexItem>
                    )}
                </EuiFlexGroup>
                <EuiSpacer size="xs" />
              </React.Fragment>
            ))}
            <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false} wrap>
              <EuiFlexItem grow={false} style={{ minWidth: 344 }}>
                <EuiFieldNumber
                  aria-label={translate(`${i18n}.onSite.zones.beyond`, {
                    defaultMessage: "Per further km beyond the last zone (€)",
                  })}
                  value={config.onSite.perKmBeyond}
                  min={0}
                  step={0.1}
                  onChange={(e) =>
                    updateOnSite("perKmBeyond", parseFloat(e.target.value) || 0)
                  }
                  prepend={translate(`${i18n}.onSite.zones.beyondPrefix`, {
                    defaultMessage: "each further km",
                  })}
                  append="€"
                  compressed
                />
              </EuiFlexItem>
              {calculations.onSite.travel.zone < 0 && (
                <EuiFlexItem grow={false}>
                  <EuiBadge color="primary">
                    {translate(`${i18n}.onSite.zones.active`, {
                      defaultMessage: "applies",
                    })}
                  </EuiBadge>
                </EuiFlexItem>
              )}
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  size="xs"
                  iconType="plusInCircle"
                  onClick={() => {
                    const zones = config.onSite.travelZones;
                    const last = zones[zones.length - 1];
                    updateOnSite("travelZones", [
                      ...zones,
                      {
                        upToKm: (last?.upToKm ?? 0) * 2 || 50,
                        flat: (last?.flat ?? 0) * 2 || 100,
                      },
                    ]);
                  }}
                >
                  {translate(`${i18n}.onSite.zones.add`, {
                    defaultMessage: "Add zone",
                  })}
                </EuiButtonEmpty>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size="s" />
            <EuiText
              size="xs"
              color={
                calculations.onSite.travel.charge <
                calculations.onSite.travelEffortCost
                  ? "warning"
                  : "subdued"
              }
            >
              {translate(`${i18n}.onSite.zones.result`, {
                values: {
                  distance: config.onSite.distanceKm,
                  roundTrip: calculations.onSite.roundTripKm,
                  hours: calculations.onSite.travelHours,
                  charge: euro(calculations.onSite.travel.charge),
                  effort: euro(calculations.onSite.travelEffortCost),
                },
                defaultMessage:
                  "{{distance}} km away: {{roundTrip}} km and {{hours}} h travel per visit. Travel charge €{{charge}} - by effort it would be €{{effort}}.",
              })}
            </EuiText>
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
              <p>
                {translate(`${i18n}.onSite.breakdown`, {
                  values: {
                    labour: euro(calculations.onSite.labourPerVisit),
                    travel: euro(calculations.onSite.travelPerVisit),
                    meals: euro(calculations.onSite.mealAllowancePerVisit),
                    perVisit: euro(calculations.onSite.perVisit),
                    perMonth: euro(calculations.onSite.perMonth),
                  },
                  defaultMessage:
                    "Per visit: €{{labour}} working time + €{{travel}} travel costs + €{{meals}} meal allowance = €{{perVisit}}; spread over the year €{{perMonth}} per month.",
                })}
              </p>
              <p>
                {translate(`${i18n}.onSite.legal`, {
                  defaultMessage:
                    "Meal allowance per §9 Abs. 4a EStG: €14 for more than 8 hours away and for arrival/departure days, €28 per full day. Travel time you drive yourself is working time.",
                })}
              </p>
            </EuiText>
            {/*
              The one rule that makes a visit impossible rather than expensive:
              on site plus travel may not exceed ten hours a day.
            */}
            {calculations.onSite.exceedsDailyLimit && (
              <>
                <EuiSpacer size="s" />
                <EuiCallOut
                  size="s"
                  color="warning"
                  iconType="alert"
                  title={translate(`${i18n}.onSite.dailyLimit`, {
                    values: { hours: calculations.onSite.hoursPerDay },
                    defaultMessage:
                      "{{hours}} h per day exceeds the 10-hour maximum working day (§3 ArbZG) - plan an overnight stay.",
                  })}
                />
              </>
            )}
          </>
        )}

        {/*
          Development on customer request: a yearly budget of hours, priced at
          its own rate with an uplift for everything around the coding. A
          budget rather than open-ended effort, so the price is fixed and so is
          the commitment behind it.
        */}
        <EuiHorizontalRule margin="m" />
        <SubGroup
          label={translate(`${i18n}.customDev.title`, {
            defaultMessage: "Development on customer request (extension)",
          })}
        />
        <EuiSwitch
          label={translate(`${i18n}.customDev.include`, {
            defaultMessage: "Include a development budget",
          })}
          checked={config.customDevelopment.included}
          onChange={(e) =>
            updateCustomDevelopment("included", e.target.checked)
          }
          compressed
        />
        {config.customDevelopment.included && (
          <>
            <EuiSpacer size="s" />
            <EuiFlexGrid columns={3} gutterSize="s">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.customDev.field.hoursPerYear`, {
                    defaultMessage: "Hours per contract year",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.customDevelopment.hoursPerYear}
                    min={0}
                    step={5}
                    onChange={(e) =>
                      updateCustomDevelopment(
                        "hoursPerYear",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    append="h"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.customDev.field.hourlyRate`, {
                    defaultMessage: "Development hourly rate (€)",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.customDevelopment.hourlyRate}
                    min={0}
                    step={5}
                    onChange={(e) =>
                      updateCustomDevelopment(
                        "hourlyRate",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    prepend="€"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(`${i18n}.customDev.field.overheadPercent`, {
                    defaultMessage: "Specification, testing & acceptance",
                  })}
                  fullWidth
                >
                  <EuiFieldNumber
                    value={config.customDevelopment.overheadPercent}
                    min={0}
                    step={5}
                    onChange={(e) =>
                      updateCustomDevelopment(
                        "overheadPercent",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    append="%"
                    compressed
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGrid>
            <EuiSpacer size="s" />
            <EuiSwitch
              label={translate(`${i18n}.customDev.expire`, {
                defaultMessage:
                  "Unused hours expire at the end of the contract year",
              })}
              checked={config.customDevelopment.unusedHoursExpire}
              onChange={(e) =>
                updateCustomDevelopment("unusedHoursExpire", e.target.checked)
              }
              compressed
            />
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
              <p>
                {translate(`${i18n}.customDev.breakdown`, {
                  values: {
                    hours: calculations.customDevelopment.hoursPerYear,
                    effort: calculations.customDevelopment.effortHoursPerYear,
                    perYear: euro(calculations.customDevelopment.perYear),
                    perMonth: euro(calculations.customDevelopment.perMonth),
                  },
                  defaultMessage:
                    "{{hours}} h budget = {{effort}} h effort incl. overhead = €{{perYear}} per year, €{{perMonth}} per month. Requests beyond the budget are quoted separately.",
                })}
              </p>
            </EuiText>
          </>
        )}

        <EuiHorizontalRule margin="m" />
        <EuiFlexGrid columns={2} gutterSize="m">
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.hourlyRate`, {
                defaultMessage: "Hourly rate (€)",
              })}
              fullWidth
            >
              <EuiFieldNumber
                value={config.hourlyRate}
                onChange={(e) =>
                  updateConfig("hourlyRate", parseFloat(e.target.value) || 0)
                }
                prepend="€"
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.initialSetupHours`, {
                defaultMessage: "Initial setup (one-off)",
              })}
              fullWidth
            >
              <EuiFieldNumber
                value={config.effort.initialSetupHours}
                min={0}
                onChange={(e) =>
                  updateSetupHours(parseFloat(e.target.value) || 0)
                }
                append="h"
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
        </EuiFlexGrid>

        {/* What those fields actually come to. */}
        <EuiHorizontalRule margin="m" />
        <EuiText size="xs" color="subdued">
          <p>
            {translate(`${i18n}.services.breakdown`, {
              values: {
                hours: calculations.effortHours.total,
                cost: euro(calculations.monthlyOperatingCost),
              },
              defaultMessage:
                "{{hours}} h services per month, billed at €{{cost}}. Weekly effort is converted through the year (52 weeks, not 48).",
            })}
          </p>
          <p>
            {translate(`${i18n}.coverage.breakdown`, {
              values: {
                tier: coverageLabel(calculations.coverageTier),
                cost: euro(calculations.coverageFee),
              },
              defaultMessage:
                "Service hours {{tier}}: fixed €{{cost}} per month, independent of the hours worked.",
            })}
          </p>
          {calculations.outOfHours.hours > 0 && (
            <p>
              {translate(`${i18n}.outOfHours.breakdown`, {
                values: {
                  hours: calculations.outOfHours.hours,
                  percent: calculations.outOfHours.averageSurchargePercent,
                  cost: euro(calculations.outOfHours.cost),
                },
                defaultMessage:
                  "Call-outs outside business hours: {{hours}} h plus {{percent}}% average surcharge = €{{cost}} per month.",
              })}
            </p>
          )}
          <p>
            {translate(`${i18n}.effort.setup`, {
              values: {
                hours: config.effort.initialSetupHours,
                cost: euro(calculations.setupCost),
              },
              defaultMessage:
                "Initial setup {{hours}} h = €{{cost}}, recovered once alongside the hardware.",
            })}
          </p>
        </EuiText>
      </CostGroup>

      <EuiSpacer size="s" />

      {/* ------------------------------------------------------------------ */}
      {/* Platform features                                                   */}
      {/* ------------------------------------------------------------------ */}
      <CostGroup
        id="cost-group-features"
        title={translate(`${i18n}.features.title`, {
          defaultMessage: "Features",
        })}
        note={translate(`${i18n}.features.badge`, {
          values: { count: calculations.features.enabled.length },
          defaultMessage: "{{count}} enabled",
        })}
        figure={`€${euro(calculations.features.perMonth)}`}
      >
        {/*
          What the customer is sold and sees. Prefilled from the questionnaire,
          which stays what the appliance is deployed from: a switch here changes
          the price and the offer, not the appliance. The deployment-only
          switches of that section (BGP, CAPI, OpenSearch tiers, ...) are not
          here - the customer does not know them and they are not sold.
        */}
        <EuiText size="xs" color="subdued">
          <p>
            {translate(`${i18n}.features.intro`, {
              defaultMessage:
                "Prefilled from the questionnaire's Features section; switching one here changes the price and the offer, not the deployment. AI/ML is priced at the agreed monthly spend cap.",
            })}
          </p>
        </EuiText>
        <EuiSpacer size="s" />
        {FEATURE_IDS.map((id) => {
          const line = config.features[id];
          const inQuestionnaire = questionnaireFeatures[id].enabled;
          return (
            <React.Fragment key={id}>
              <EuiFlexGroup alignItems="center" gutterSize="m" wrap>
                <EuiFlexItem grow={2} style={{ minWidth: 240 }}>
                  <EuiSwitch
                    label={
                      <>
                        <strong>
                          {translate(`${i18n}.features.${id}`, {
                            defaultMessage: id,
                          })}
                        </strong>
                        {/* Say so when the price and the deployment disagree. */}
                        {line.enabled !== inQuestionnaire && (
                          <EuiText size="xs" color="warning">
                            {inQuestionnaire
                              ? translate(`${i18n}.features.onInQuestionnaire`, {
                                  defaultMessage: "Enabled in the questionnaire",
                                })
                              : translate(`${i18n}.features.offInQuestionnaire`, {
                                  defaultMessage: "Disabled in the questionnaire",
                                })}
                          </EuiText>
                        )}
                      </>
                    }
                    checked={line.enabled}
                    onChange={(e) =>
                      updateFeature(id, { enabled: e.target.checked })
                    }
                    compressed
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={1} style={{ minWidth: 150 }}>
                  <EuiFieldNumber
                    aria-label={translate(`${i18n}.features.oneOff`, {
                      defaultMessage: "One-off (€)",
                    })}
                    value={line.oneOff}
                    min={0}
                    step={50}
                    disabled={!line.enabled}
                    onChange={(e) =>
                      updateFeature(id, {
                        oneOff: parseFloat(e.target.value) || 0,
                      })
                    }
                    prepend="€"
                    append={translate(`${i18n}.features.once`, {
                      defaultMessage: "once",
                    })}
                    compressed
                    fullWidth
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={1} style={{ minWidth: 150 }}>
                  <EuiFieldNumber
                    aria-label={translate(`${i18n}.features.monthly`, {
                      defaultMessage: "Monthly (€)",
                    })}
                    value={line.monthly}
                    min={0}
                    step={10}
                    disabled={!line.enabled}
                    onChange={(e) =>
                      updateFeature(id, {
                        monthly: parseFloat(e.target.value) || 0,
                      })
                    }
                    prepend="€"
                    append={translate(`${i18n}.services.perMonth`, {
                      defaultMessage: "/ month",
                    })}
                    compressed
                    fullWidth
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size="s" />
            </React.Fragment>
          );
        })}
        <EuiHorizontalRule margin="m" />
        <EuiText size="xs" color="subdued">
          <p>
            {translate(`${i18n}.features.breakdown`, {
              values: {
                perMonth: euro(calculations.features.perMonth),
                oneOff: euro(calculations.features.oneOff),
              },
              defaultMessage:
                "€{{perMonth}} per month; one-off €{{oneOff}}, recovered alongside the hardware.",
            })}
          </p>
        </EuiText>
      </CostGroup>

      <EuiSpacer size="s" />

      {/* ------------------------------------------------------------------ */}
      {/* Development share                                                   */}
      {/* ------------------------------------------------------------------ */}
      <CostGroup
        id="cost-group-development"
        title={translate(`${i18n}.development.title`, {
          defaultMessage: "Development cost",
        })}
        note={translate(`${i18n}.group.development.badge`, {
          defaultMessage: "Share per appliance",
        })}
        figure={`€${euro(calculations.development.perMonth)}`}
      >
        <EuiText size="xs" color="subdued">
          <p>
            {translate(`${i18n}.development.intro`, {
              defaultMessage:
                "The platform is built once and sold many times. An appliance priced on hardware and hours alone is priced below what it cost to be able to sell it at all, so each one recovers a share.",
            })}
          </p>
        </EuiText>
        <EuiSpacer size="m" />

        <EuiFlexGrid columns={2} gutterSize="m">
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.totalInvestment`, {
                defaultMessage: "Development investment to recover (€)",
              })}
              helpText={translate(`${i18n}.development.source`, {
                defaultMessage:
                  "Default: the cumulative investment since founding recorded in the internal capital and staffing plan - a reported figure, not an estimate. Internal only.",
              })}
              fullWidth
            >
              <EuiFieldNumber
                value={config.development.totalInvestment}
                min={0}
                step={50000}
                onChange={(e) =>
                  updateDevelopment(
                    "totalInvestment",
                    parseFloat(e.target.value) || 0,
                  )
                }
                prepend="€"
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.amortisationYears`, {
                defaultMessage: "Recovered over",
              })}
              fullWidth
            >
              <EuiFieldNumber
                value={config.development.amortisationYears}
                min={1}
                onChange={(e) =>
                  updateDevelopment(
                    "amortisationYears",
                    parseFloat(e.target.value) || 0,
                  )
                }
                append={translate(`${i18n}.unit.years`, {
                  defaultMessage: "years",
                })}
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.applianceCount`, {
                defaultMessage: "Shared across appliances",
              })}
              helpText={translate(`${i18n}.development.applianceHelp`, {
                defaultMessage:
                  "Fewer appliances load each one with a larger share.",
              })}
              fullWidth
            >
              <EuiFieldNumber
                value={config.development.applianceCount}
                min={1}
                onChange={(e) =>
                  updateDevelopment(
                    "applianceCount",
                    parseFloat(e.target.value) || 0,
                  )
                }
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
        </EuiFlexGrid>

        <EuiHorizontalRule margin="m" />
        <EuiText size="xs" color="subdued">
          <p>
            {translate(`${i18n}.development.share`, {
              values: {
                perYear: euro(calculations.development.perYear),
                perMonth: euro(calculations.development.perMonth),
              },
              defaultMessage:
                "€{{perYear}} per appliance per year = €{{perMonth}} per month",
            })}
          </p>
          <p>
            {translate(`${i18n}.development.term`, {
              values: {
                term: yearLabel(contractYears),
                cost: euro(calculations.development.perTerm),
              },
              defaultMessage: "Over {{term}} this contract recovers €{{cost}}.",
            })}
          </p>
        </EuiText>
      </CostGroup>

      <EuiSpacer size="s" />

      {/* ------------------------------------------------------------------ */}
      {/* Margin and discounts                                                */}
      {/* ------------------------------------------------------------------ */}
      <CostGroup
        id="cost-group-pricing"
        title={translate(`${i18n}.calc.title`, {
          defaultMessage: "Calculation & Discounts",
        })}
        note={translate(`${i18n}.group.pricing.badge`, {
          values: {
            margin: config.profitMargin,
            discount: calculations.yearlyDiscount,
          },
          defaultMessage: "Margin {{margin}} % · term discount {{discount}} %",
        })}
        // What the discounts take off the monthly price - the group's own
        // effect, not a cost. Shown as a reduction because that is the
        // direction it moves the figure in the bar above.
        figure={`−€${euro(
          Math.max(
            termComparison.undiscountedMonthly - calculations.finalMonthlyCost,
            0,
          ),
        )}`}
        figureColor="success"
      >
        <EuiFlexGrid columns={2} gutterSize="m">
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.profitMargin`, {
                values: { percent: config.profitMargin },
                defaultMessage: "Profit margin ({{percent}}%)",
              })}
              fullWidth
            >
              <EuiRange
                min={0}
                max={50}
                step={1}
                value={config.profitMargin}
                onChange={(e) =>
                  updateConfig("profitMargin", parseFloat(e.currentTarget.value))
                }
                showValue
                append="%"
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiFormRow
              label={translate(`${i18n}.field.upfrontDiscount`, {
                values: { percent: config.upfrontPaymentDiscount },
                defaultMessage: "Upfront payment discount ({{percent}}%)",
              })}
              fullWidth
            >
              <EuiRange
                min={0}
                max={20}
                step={0.5}
                value={config.upfrontPaymentDiscount}
                onChange={(e) =>
                  updateConfig(
                    "upfrontPaymentDiscount",
                    parseFloat(e.currentTarget.value),
                  )
                }
                showValue
                append="%"
                fullWidth
              />
            </EuiFormRow>
          </EuiFlexItem>

          {/*
            One slider per term beyond the first, each its own grid cell.
            Generated from CONTRACT_TERMS so a new term brings its own control
            rather than being silently undiscountable.
          */}
          {CONTRACT_TERMS.filter((years) => years > 1).map((years) => (
            <EuiFlexItem key={years}>
              <EuiFormRow
                label={translate(`${i18n}.field.discountYears`, {
                  values: { years, percent: config.termDiscounts[years] ?? 0 },
                  defaultMessage: "Discount {{years}} years ({{percent}}%)",
                })}
                fullWidth
              >
                <EuiRange
                  min={0}
                  // Room to discount a longer term harder, which is the point of
                  // offering one.
                  max={15 + years * 5}
                  step={0.5}
                  value={config.termDiscounts[years] ?? 0}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      termDiscounts: {
                        ...prev.termDiscounts,
                        [years]: parseFloat(e.currentTarget.value) || 0,
                      },
                    }))
                  }
                  showValue
                  append="%"
                  fullWidth
                />
              </EuiFormRow>
            </EuiFlexItem>
          ))}
        </EuiFlexGrid>
      </CostGroup>

      <EuiSpacer size="s" />

      {/* ------------------------------------------------------------------ */}
      {/* Fitting the offer to the asking price                              */}
      {/* ------------------------------------------------------------------ */}
      <CostGroup
        id="cost-group-adjustment"
        title={translate(`${i18n}.adjust.title`, {
          defaultMessage: "Adjust to asking price",
        })}
        note={
          calculations.adjustment.direction === "uplift"
            ? translate(`${i18n}.adjust.badgeUplift`, {
                values: { gap: euro(calculations.adjustment.gap) },
                defaultMessage: "+€{{gap}} above the rate",
              })
            : calculations.adjustment.direction === "reduce"
              ? translate(`${i18n}.adjust.badge`, {
                  values: { gap: euro(calculations.adjustment.gap) },
                  defaultMessage: "gap €{{gap}}",
                })
              : translate(`${i18n}.adjust.badgeNone`, {
                  defaultMessage: "no asking price",
                })
        }
        figure={`€${euro(calculations.adjustment.adjustedTotal)}`}
        figureColor={
          calculations.adjustment.direction === "uplift" ? "success" : undefined
        }
        initialIsOpen={calculations.adjustment.applies}
      >
        {/*
          The weight says how much a line may give way, not by how much: the
          gap to the asking price is spread by weight x price, and what one line
          cannot carry flows on to the others. 0 fixes a line.
        */}
        <EuiText size="xs" color="subdued">
          <p>
            {translate(`${i18n}.adjust.intro`, {
              values: { max: MAX_PRICE_WEIGHT },
              defaultMessage:
                "Weight each line by how much it may give way when the price has to come down to the customer's asking price: 0 keeps it fixed, {{max}} lets it carry the most. The gap is spread by weight × price.",
            })}
          </p>
        </EuiText>
        <EuiSpacer size="s" />
        {calculations.adjustment.applies && calculations.adjustment.shortfall > 0 && (
          <>
            <EuiCallOut
              size="s"
              color="danger"
              iconType="alert"
              title={translate(`${i18n}.adjust.shortfall`, {
                values: {
                  shortfall: euro(calculations.adjustment.shortfall),
                },
                defaultMessage:
                  "The weighted lines cannot close the gap - €{{shortfall}} per month remain. Give fixed lines a weight to reach the asking price.",
              })}
            />
            <EuiSpacer size="s" />
          </>
        )}
        <div style={{ overflowX: "auto" }}>
          <EuiBasicTable<AdjustedPriceLine>
            items={calculations.adjustment.lines}
            itemId={(line) => `${line.key.kind}:${line.key.id}`}
            columns={[
              {
                field: "key",
                name: translate(`${i18n}.adjust.col.line`, {
                  defaultMessage: "Line",
                }),
                render: (key: PriceLineKey, line: AdjustedPriceLine) => (
                  <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
                    <EuiFlexItem grow={false}>{priceLineLabel(key)}</EuiFlexItem>
                    {line.belowCost && (
                      <EuiFlexItem grow={false}>
                        <EuiBadge color="warning">
                          {translate(`${i18n}.adjust.belowCost`, {
                            defaultMessage: "below cost",
                          })}
                        </EuiBadge>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                ),
              },
              {
                field: "weight",
                name: translate(`${i18n}.adjust.col.weight`, {
                  defaultMessage: "Weight",
                }),
                width: "110px",
                render: (_: number, line: AdjustedPriceLine) => (
                  <EuiFieldNumber
                    aria-label={translate(`${i18n}.adjust.col.weight`, {
                      defaultMessage: "Weight",
                    })}
                    value={
                      line.key.kind === "feature"
                        ? config.priceWeights.features[line.key.id]
                        : config.priceWeights.lines[line.key.id]
                    }
                    min={0}
                    max={MAX_PRICE_WEIGHT}
                    step={1}
                    onChange={(e) =>
                      updateWeight(
                        line.key,
                        Math.min(
                          Math.max(parseFloat(e.target.value) || 0, 0),
                          MAX_PRICE_WEIGHT,
                        ),
                      )
                    }
                    compressed
                  />
                ),
              },
              {
                field: "price",
                name: translate(`${i18n}.adjust.col.price`, {
                  defaultMessage: "Calculated",
                }),
                align: "right",
                render: (value: number) => `€${euro(value)}`,
              },
              {
                field: "reduction",
                name:
                  calculations.adjustment.direction === "uplift"
                    ? translate(`${i18n}.adjust.col.uplift`, {
                        defaultMessage: "Uplift",
                      })
                    : translate(`${i18n}.adjust.col.reduction`, {
                        defaultMessage: "Reduction",
                      }),
                align: "right",
                render: (value: number, line: AdjustedPriceLine) =>
                  line.uplift > 0 ? (
                    <span className="euiTextColor--success">+€{euro(line.uplift)}</span>
                  ) : value > 0 ? (
                    `−€${euro(value)}`
                  ) : (
                    "-"
                  ),
              },
              {
                field: "adjusted",
                name: translate(`${i18n}.adjust.col.adjusted`, {
                  defaultMessage: "Adjusted",
                }),
                align: "right",
                render: (value: number) => <strong>€{euro(value)}</strong>,
              },
            ]}
          />
        </div>
        <EuiSpacer size="s" />
        <EuiText size="xs" color="subdued">
          {calculations.adjustment.direction === "uplift"
            ? translate(`${i18n}.adjust.resultUplift`, {
                values: {
                  quoted: euro(calculations.adjustment.quoted),
                  target: euro(calculations.adjustment.target),
                  gap: euro(calculations.adjustment.gap),
                },
                defaultMessage:
                  "The customer is willing to pay €{{target}} - €{{gap}} more than the calculated €{{quoted}}. The offer quotes their price; the uplift is spread by these weights and stated as its own line.",
              })
            : calculations.adjustment.applies
            ? translate(`${i18n}.adjust.result`, {
                values: {
                  quoted: euro(calculations.adjustment.quoted),
                  target: euro(calculations.adjustment.target),
                  adjusted: euro(calculations.adjustment.adjustedTotal),
                },
                defaultMessage:
                  "Calculated €{{quoted}} → asking price €{{target}}: the adjusted offer comes to €{{adjusted}} per month. The approval PDF shows this table.",
              })
            : translate(`${i18n}.adjust.none`, {
                defaultMessage:
                  "Enter the customer's asking price above. Once it is below the calculated rate, the gap is spread across the lines here.",
              })}
        </EuiText>
      </CostGroup>

      <EuiSpacer size="m" />

      {/* ------------------------------------------------------------------ */}
      {/* Break-even and savings                                              */}
      {/* ------------------------------------------------------------------ */}
      <EuiFlexGroup gutterSize="m">
        <EuiFlexItem>
          <EuiPanel hasBorder hasShadow={false}>
            <EuiStat
              title={translate(`${i18n}.breakEven.months`, {
                values: { count: calculations.breakEvenMonths.toFixed(1) },
                defaultMessage: "{{count}} months",
              })}
              description={translate(`${i18n}.breakEven.title`, {
                defaultMessage: "Break-Even Analysis",
              })}
              titleSize="m"
              titleColor={
                calculations.breakEvenMonths <= 12 ? "success" : "warning"
              }
              reverse
            />
            <EuiSpacer size="xs" />
            <EuiText
              size="xs"
              color={calculations.breakEvenMonths <= 12 ? "success" : "warning"}
            >
              {calculations.breakEvenMonths <= 12
                ? translate(`${i18n}.breakEven.met`, {
                    defaultMessage:
                      "✓ Target met: amortization within one year",
                  })
                : translate(`${i18n}.breakEven.exceeded`, {
                    defaultMessage:
                      "⚠ Amortization takes longer than 12 months",
                  })}
            </EuiText>
          </EuiPanel>
        </EuiFlexItem>

        <EuiFlexItem>
          <EuiPanel hasBorder hasShadow={false}>
            <EuiStat
              title={`€${euro(calculations.totalSavings)}`}
              description={translate(`${i18n}.savings.title`, {
                defaultMessage: "Total Savings",
              })}
              titleSize="m"
              titleColor={
                calculations.totalSavings > 0 ? "success" : "default"
              }
              reverse
            />
            <EuiSpacer size="xs" />
            <EuiText size="xs" color="subdued">
              {translate(`${i18n}.savings.lead`, {
                defaultMessage: "Through discounts you save a total of",
              })}{" "}
              {translate(`${i18n}.savings.over`, {
                values: { term: yearLabel(contractYears) },
                defaultMessage: "over {{term}}",
              })}
            </EuiText>
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="m" />

      {/* ------------------------------------------------------------------ */}
      {/* Term comparison                                                     */}
      {/* ------------------------------------------------------------------ */}
      <EuiPanel hasBorder hasShadow={false} paddingSize="m">
        <EuiTitle size="xs">
          <h3>
            {translate(`${i18n}.comparison.title`, {
              defaultMessage: "Comparison of Contract Terms",
            })}
          </h3>
        </EuiTitle>
        <EuiSpacer size="s" />

        {/*
          A table rather than a card per term: it takes one row per term however
          many terms there are, it survives long German column headings, and it
          puts the four figures in columns you can actually read down.
        */}
        <div style={{ overflowX: "auto" }}>
          <EuiBasicTable<TermComparisonRow>
            items={termComparison.rows}
            rowProps={(row) => ({
              className:
                row.years === contractYears
                  ? "euiTableRow-isSelected euiTableRow-isClickable"
                  : "euiTableRow-isClickable",
              onClick: () => setContractYears(row.years),
            })}
            columns={[
              {
                field: "years",
                name: translate(`${i18n}.comparison.col.term`, {
                  defaultMessage: "Term",
                }),
                render: (years: number) => (
                  <EuiFlexGroup
                    alignItems="center"
                    gutterSize="s"
                    responsive={false}
                  >
                    <EuiFlexItem grow={false}>
                      <strong>{yearLabel(years)}</strong>
                    </EuiFlexItem>
                    {years === contractYears && (
                      <EuiFlexItem grow={false}>
                        <EuiBadge color="primary">
                          {translate(`${i18n}.comparison.row.selected`, {
                            defaultMessage: "selected",
                          })}
                        </EuiBadge>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                ),
              },
              {
                field: "monthly",
                name: translate(`${i18n}.summary.monthly`, {
                  defaultMessage: "Monthly",
                }),
                align: "right",
                render: (value: number) => `€${euro(value)}`,
              },
              {
                field: "yearly",
                name: translate(`${i18n}.summary.yearly`, {
                  defaultMessage: "Yearly",
                }),
                align: "right",
                render: (value: number) => `€${euro(value)}`,
              },
              {
                field: "total",
                name: translate(`${i18n}.comparison.col.total`, {
                  defaultMessage: "Total",
                }),
                align: "right",
                render: (value: number) => `€${euro(value)}`,
              },
              {
                field: "savings",
                name: translate(`${i18n}.comparison.col.savings`, {
                  defaultMessage: "Savings",
                }),
                align: "right",
                // A term that saves nothing shows a dash rather than "€0,00":
                // the reference row is not a saving of zero, it is the thing
                // the others are measured against.
                render: (value: number) =>
                  value > 0 ? (
                    <span className="euiTextColor--success">€{euro(value)}</span>
                  ) : (
                    <EuiText size="s" color="subdued">
                      -
                    </EuiText>
                  ),
              },
            ]}
          />
        </div>
      </EuiPanel>
    </div>
  );
}
